import React, { useState, useEffect, useRef, lazy, Suspense, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Trash2, Zap, Battery, Sun, Home, Car, Fan,
  MousePointer2, Cable, Settings2, Video, 
  Circle, Square as SquareIcon, MoreHorizontal,
  Save, Upload, Camera, Image as ImageIcon,
  ZoomIn, ZoomOut, Undo, Redo, Building2, Menu, X,
  Map as MapIcon, Activity, Moon, Sun as SunIcon, PlayCircle,
  Plus, Check, RefreshCw, Grid3x3, Download, Palette,
  CloudRain, Cloud, Clock, TrendingUp, AlertTriangle, Plane,
  RotateCcw, Eraser, Link as LinkIcon, ExternalLink, Box, Layers,
  BarChart3, Eye, ArrowDown, PanelLeft, ChevronDown, Pipette, FolderOpen, Database
} from 'lucide-react';
import { CameraPreset, CAMERA_PRESETS } from './energy-flow-3d/types';
import { useUndoRedo } from '@/hooks/useUndoRedo';
import { saveScene, listScenes, loadScene, SavedScene, LibraryAsset } from '@/lib/energyFlowSupabaseStorage';
import AssetLibraryManager from './AssetLibraryManager';

// Lazy load 3D view for performance
const EnergyFlow3DView = lazy(() => import('./energy-flow-3d/EnergyFlow3DView'));

// --- TYPES & DATA ---

type AssetType = 'solar' | 'battery' | 'grid' | 'house' | 'ev_charger' | 'heatpump' | 'custom';
type FlowShape = 'dash' | 'circle' | 'square';
type LineType = 'curved' | 'straight' | 'orthogonal';
type ViewMode = '2d' | '3d';

interface Node {
  id: string; type: AssetType; x: number; y: number;
  label?: string; 
  customImageSrc?: string | null; 
  customModelUrl?: string; // For 3D GLTF models (.glb, .gltf)
  scale?: number;
  baseValue?: number;
  currentValue?: number;
  batteryCharge?: number; 
  status?: 'ok' | 'warning' | 'error';
  // Animation props
  animScale?: number; 
  pulsePhase?: number; 
  _displayScale?: number;
  // Click-through props
  clickUrl?: string;
  clickTarget?: '_self' | '_blank' | '_parent';
}

interface Connection {
  id: string; fromId: string; toId: string; color: string;
  speed: number; width: number; direction: 'forward' | 'reverse'; shape: FlowShape;
  activeInDay: boolean; activeInNight: boolean;
  load?: number;
  // New properties
  lineType: LineType;
  particleColor: string;
  particleSize: number;
}

interface AppState {
    nodes: Node[];
    connections: Connection[];
    library: Record<string, string[]>;
    backgroundSrc: string | null;
    backgroundColor: string;
    showGrid: boolean;
    weatherValue: number; 
    timeOfDay: number; 
    version: number;
}

interface GraphData {
    production: number;
    consumption: number;
    batteryLevel: number;
}

interface KPIData {
    selfSufficiency: number;
    costSaved: number;
    co2Saved: number;
}

const SPRITE_CONFIG: Record<string, string | null> = {
    cloud: null,
    bird: null,
    airplane: null,
    rain: null
};

const TILE_WIDTH = 64;
const TILE_HEIGHT = 32;
const GRID_SIZE = 30;

// Default sizes per asset type (in grid units and scale)
const ASSET_DEFAULTS: Record<AssetType, { gridWidth: number; gridHeight: number; defaultScale: number }> = {
  grid: { gridWidth: 1, gridHeight: 1, defaultScale: 1 },
  solar: { gridWidth: 2, gridHeight: 2, defaultScale: 1 },
  battery: { gridWidth: 1, gridHeight: 1, defaultScale: 1 },
  house: { gridWidth: 4, gridHeight: 2, defaultScale: 1.2 },
  ev_charger: { gridWidth: 1, gridHeight: 1, defaultScale: 0.8 },
  heatpump: { gridWidth: 1, gridHeight: 1, defaultScale: 0.9 },
  custom: { gridWidth: 1, gridHeight: 1, defaultScale: 1 },
};

const ASSETS: { type: AssetType; label: string; icon: React.ReactNode; defaultKw: number }[] = [
  { type: 'grid', label: 'Net', icon: <Zap size={18} />, defaultKw: 0 },
  { type: 'solar', label: 'Veld', icon: <Sun size={18} />, defaultKw: 25 },
  { type: 'battery', label: 'GV Accu', icon: <Battery size={18} />, defaultKw: 10 },
  { type: 'house', label: 'Bedrijf', icon: <Building2 size={18} />, defaultKw: 12 },
  { type: 'ev_charger', label: 'Laadpaal', icon: <Car size={18} />, defaultKw: 11 },
  { type: 'heatpump', label: 'WP', icon: <Fan size={18} />, defaultKw: 5 },
  { type: 'custom', label: 'Overig', icon: <ImageIcon size={18} />, defaultKw: 0 },
];

const PRESETS: {name: string, nodes: Node[], connections: Connection[]}[] = [
    {
        name: "Smart Home Basis",
        nodes: [
            { id: "p1", type: "house", x: 15, y: 15, label: "Woning", baseValue: 4, animScale: 1 },
            { id: "p2", type: "solar", x: 12, y: 12, label: "Dak Zuid", baseValue: 6, animScale: 1 },
            { id: "p3", type: "grid", x: 18, y: 18, label: "Liander", baseValue: 0, animScale: 1 }
        ],
        connections: [
            { id: "c1", fromId: "p2", toId: "p1", color: "#22c55e", speed: 3, width: 4, direction: "forward", shape: "circle", activeInDay: true, activeInNight: false, lineType: 'curved', particleColor: '#ffffff', particleSize: 2 },
            { id: "c2", fromId: "p3", toId: "p1", color: "#ef4444", speed: 2, width: 2, direction: "forward", shape: "dash", activeInDay: false, activeInNight: true, lineType: 'curved', particleColor: '#ffffff', particleSize: 2 }
        ]
    },
    {
        name: "Industrieel Laadplein",
        nodes: [
            { id: "i1", type: "house", x: 15, y: 10, label: "Hoofdkantoor", baseValue: 50, animScale: 1 },
            { id: "i2", type: "solar", x: 10, y: 5, label: "Solar Carport", baseValue: 120, animScale: 1 },
            { id: "i3", type: "battery", x: 18, y: 12, label: "MegaPack", baseValue: 200, batteryCharge: 60, animScale: 1 },
            { id: "i4", type: "ev_charger", x: 20, y: 18, label: "Snelladers", baseValue: 80, animScale: 1 },
            { id: "i5", type: "grid", x: 25, y: 25, label: "Trafo", baseValue: 0, animScale: 1 }
        ],
        connections: [
            { id: "ic1", fromId: "i2", toId: "i3", color: "#22c55e", speed: 4, width: 6, direction: "forward", shape: "circle", activeInDay: true, activeInNight: false, lineType: 'curved', particleColor: '#ffffff', particleSize: 2 },
            { id: "ic2", fromId: "i3", toId: "i4", color: "#3b82f6", speed: 5, width: 5, direction: "forward", shape: "square", activeInDay: true, activeInNight: true, lineType: 'straight', particleColor: '#ffffff', particleSize: 2 },
            { id: "ic3", fromId: "i5", toId: "i1", color: "#ef4444", speed: 2, width: 3, direction: "forward", shape: "dash", activeInDay: true, activeInNight: true, lineType: 'orthogonal', particleColor: '#ffffff', particleSize: 2 }
        ]
    }
];

// --- MATH & COLOR HELPERS ---

const isoToScreen = (x: number, y: number, zoom: number, offset: {x: number, y: number}) => ({
  x: ((x - y) * TILE_WIDTH / 2) * zoom + offset.x,
  y: ((x + y) * TILE_HEIGHT / 2) * zoom + offset.y
});

const screenToIso = (screenX: number, screenY: number, zoom: number, offset: {x: number, y: number}) => {
  const adjX = (screenX - offset.x) / zoom;
  const adjY = (screenY - offset.y) / zoom;
  const y = (adjY / (TILE_HEIGHT / 2) - adjX / (TILE_WIDTH / 2)) / 2;
  const x = (adjY / (TILE_HEIGHT / 2) + adjX / (TILE_WIDTH / 2)) / 2;
  return { x: Math.round(x), y: Math.round(y) };
};

// Snap position to grid based on asset type (house = 2x4 blocks)
const getSnappedPosition = (rawIso: {x: number, y: number}, assetType: string): {x: number, y: number} => {
  if (assetType === 'house') {
    // House is 2x4 blocks, snap to appropriate grid
    return {
      x: Math.round(rawIso.x / 2) * 2,
      y: Math.round(rawIso.y / 4) * 4
    };
  }
  return rawIso;
};

const getBezierPoint = (t: number, p0: any, p1: any, p2: any) => {
  const mt = 1 - t;
  return {
    x: mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x,
    y: mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y
  };
};

// Get point along a line path (for straight/orthogonal)
const getLinePoint = (t: number, p0: {x: number, y: number}, p2: {x: number, y: number}, lineType: LineType) => {
  if (lineType === 'straight') {
    return {
      x: p0.x + (p2.x - p0.x) * t,
      y: p0.y + (p2.y - p0.y) * t
    };
  } else if (lineType === 'orthogonal') {
    // Two-segment orthogonal path: horizontal first, then vertical
    const midX = p2.x;
    const midY = p0.y;
    const totalLen = Math.abs(p2.x - p0.x) + Math.abs(p2.y - p0.y);
    const seg1Len = Math.abs(p2.x - p0.x);
    const seg1Ratio = totalLen > 0 ? seg1Len / totalLen : 0.5;
    
    if (t <= seg1Ratio) {
      const segT = seg1Ratio > 0 ? t / seg1Ratio : 0;
      return { x: p0.x + (midX - p0.x) * segT, y: p0.y };
    } else {
      const segT = (1 - seg1Ratio) > 0 ? (t - seg1Ratio) / (1 - seg1Ratio) : 0;
      return { x: midX, y: midY + (p2.y - midY) * segT };
    }
  }
  // Default to curved
  const p1 = { x: (p0.x + p2.x) / 2, y: (p0.y + p2.y) / 2 - 40 };
  return getBezierPoint(t, p0, p1, p2);
};

const drawIsoPrism = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, l: number, h: number, cTop: string, cLeft: string, cRight: string) => {
    ctx.beginPath(); ctx.moveTo(x, y-h); ctx.lineTo(x+w, y-h+l/2); ctx.lineTo(x, y-h+l); ctx.lineTo(x-w, y-h+l/2); ctx.closePath();
    ctx.fillStyle = cTop; ctx.fill(); ctx.strokeStyle = 'rgba(0,0,0,0.1)'; ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x, y-h+l); ctx.lineTo(x+w, y-h+l/2); ctx.lineTo(x+w, y+l/2); ctx.lineTo(x, y+l); ctx.closePath();
    ctx.fillStyle = cRight; ctx.fill();
    ctx.beginPath(); ctx.moveTo(x-w, y-h+l/2); ctx.lineTo(x, y-h+l); ctx.lineTo(x, y+l); ctx.lineTo(x-w, y+l/2); ctx.closePath();
    ctx.fillStyle = cLeft; ctx.fill();
};

const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [0,0,0];
}

const interpolateColor = (color1: number[], color2: number[], factor: number) => {
    const r = Math.round(color1[0] + factor * (color2[0] - color1[0]));
    const g = Math.round(color1[1] + factor * (color2[1] - color1[1]));
    const b = Math.round(color1[2] + factor * (color2[2] - color1[2]));
    return `rgb(${r},${g},${b})`;
}

const SKY_GRADIENTS = [
    { time: 0, top: '#020617', bottom: '#0f172a' },   
    { time: 5, top: '#1e1b4b', bottom: '#312e81' },   
    { time: 7, top: '#1e40af', bottom: '#fdba74' },   
    { time: 9, top: '#3b82f6', bottom: '#bfdbfe' },   
    { time: 12, top: '#0ea5e9', bottom: '#e0f2fe' },  
    { time: 17, top: '#2563eb', bottom: '#fdba74' },  
    { time: 19, top: '#4c1d95', bottom: '#f43f5e' },  
    { time: 21, top: '#1e1b4b', bottom: '#312e81' },  
    { time: 24, top: '#020617', bottom: '#0f172a' }   
];

const getSkyGradient = (time: number) => {
    let start = SKY_GRADIENTS[0];
    let end = SKY_GRADIENTS[SKY_GRADIENTS.length-1];
    
    for (let i = 0; i < SKY_GRADIENTS.length - 1; i++) {
        if (time >= SKY_GRADIENTS[i].time && time <= SKY_GRADIENTS[i+1].time) {
            start = SKY_GRADIENTS[i];
            end = SKY_GRADIENTS[i+1];
            break;
        }
    }
    const range = end.time - start.time;
    const progress = (time - start.time) / range;
    
    const top = interpolateColor(hexToRgb(start.top), hexToRgb(end.top), progress);
    const bottom = interpolateColor(hexToRgb(start.bottom), hexToRgb(end.bottom), progress);
    return { top, bottom };
};

export default function EnergyFlowSimulator() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const graphCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const backgroundInputRef = useRef<HTMLInputElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);
  const jsonImportRef = useRef<HTMLInputElement>(null);
  
  // Embed mode detection
  const [embedMode, setEmbedMode] = useState(false);
  const [transparentBg, setTransparentBg] = useState(false);
  
  // Data State with Undo/Redo
  const {
    state: nodes,
    set: setNodesWithHistory,
    setWithoutHistory: setNodesWithoutHistory,
    undo: undoNodes,
    redo: redoNodes,
    canUndo,
    canRedo,
    reset: resetNodes
  } = useUndoRedo<Node[]>([]);
  
  // Wrapper for setNodes that records history
  const setNodes = setNodesWithHistory;
  const nodesRef = useRef<Node[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [backgroundImg, setBackgroundImg] = useState<HTMLImageElement | null>(null);
  const [backgroundSrc, setBackgroundSrc] = useState<string | null>(null);
  const [backgroundColor, setBackgroundColor] = useState<string>('auto'); 
  const [showGrid, setShowGrid] = useState<boolean>(true);
  const [weatherValue, setWeatherValue] = useState<number>(0); 
  const [timeOfDay, setTimeOfDay] = useState<number>(12); 
  const [graphHistory, setGraphHistory] = useState<GraphData[]>([]);
  const [kpiData, setKpiData] = useState<KPIData>({ selfSufficiency: 0, costSaved: 0, co2Saved: 0 });
  
  // Library State
  const [library, setLibrary] = useState<Record<string, string[]>>({
      solar: [], battery: [], grid: [], house: [], ev_charger: [], heatpump: [], custom: []
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingAssetType, setUploadingAssetType] = useState<AssetType | null>(null);

  // View State
  const [zoom, setZoom] = useState(0.8);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [viewMode, setViewMode] = useState<ViewMode>('2d');
  const [viewTransitioning, setViewTransitioning] = useState(false);
  const [showShadows, setShowShadows] = useState(true);
  const [showEnergyStats, setShowEnergyStats] = useState(true);
  const [showWeatherEffects, setShowWeatherEffects] = useState(false);
  const [autoDayNight, setAutoDayNight] = useState(false);
  const [cameraPreset, setCameraPreset] = useState<CameraPreset>('isometric');
  const [showCameraMenu, setShowCameraMenu] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  
  // Background overlay controls for drone photos
  const [bgOverlayOffset, setBgOverlayOffset] = useState({ x: 0, y: 0 });
  const [bgOverlayScale, setBgOverlayScale] = useState(1);
  const [bgOverlayOpacity, setBgOverlayOpacity] = useState(0.6);
  const [editingBgOverlay, setEditingBgOverlay] = useState(false);
  const [showBgOverlayControls, setShowBgOverlayControls] = useState(false);
  
  // Separate GLTF upload ref
  const gltfInputRef = useRef<HTMLInputElement>(null);

  // Multi-select state
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set());
  const [isMarqueeSelecting, setIsMarqueeSelecting] = useState(false);
  const [marqueeStart, setMarqueeStart] = useState<{x: number, y: number} | null>(null);
  const [marqueeEnd, setMarqueeEnd] = useState<{x: number, y: number} | null>(null);
  const [isDraggingSelection, setIsDraggingSelection] = useState(false);
  const [dragSelectionStart, setDragSelectionStart] = useState<{x: number, y: number} | null>(null);
  
  // Color picker state
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [customHexColor, setCustomHexColor] = useState('#3b82f6');
  
  // Connection labels toggle
  const [showConnectionLabels, setShowConnectionLabels] = useState(false);

  // Interaction State
  const [selectedTool, setSelectedTool] = useState<AssetType | 'select' | 'connect'>('select');
  const [activeImageSrc, setActiveImageSrc] = useState<string | null>(null);
  const [selectedEntity, setSelectedEntity] = useState<{ type: 'node' | 'connection', id: string } | null>(null);
  const [hoverPos, setHoverPos] = useState<{ x: number, y: number } | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [connectionStart, setConnectionStart] = useState<string | null>(null);
  const [showProperties, setShowProperties] = useState(false);
  const [showWeatherControl, setShowWeatherControl] = useState(false);
  const [showResetMenu, setShowResetMenu] = useState(false);
  const [showLibraryManager, setShowLibraryManager] = useState(false);
  
  // Collapsible sidebar sections
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    assets: false,
    scene: true, // Default collapsed
  });

  // Hover Menu State
  const [hoveredAssetType, setHoveredAssetType] = useState<AssetType | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const uploadTypeRef = useRef<AssetType | null>(null);

  // Simulation State
  const [isSimulating, setIsSimulating] = useState(false);
  const [autoCycle, setAutoCycle] = useState(false);
  
  // 3D Connection state
  const [connectionStart3D, setConnectionStart3D] = useState<string | null>(null);
  
  // Save/Load CRM state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [sceneName, setSceneName] = useState('');
  const [sceneDescription, setSceneDescription] = useState('');
  const [savedScenes, setSavedScenes] = useState<SavedScene[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingScenes, setIsLoadingScenes] = useState(false);
  
  // Store initial positions for reset
  const initialPositionsRef = useRef<Map<string, {x: number, y: number}>>(new Map());
  
  // Animation Refs
  const cloudsRef = useRef<{x: number, y: number, speed: number, puffs: {x:number, y:number, r:number, alpha:number}[]}[]>([]);
  const birdsRef = useRef<{x: number, y: number, speedX: number, speedY: number, flap: number}[]>([]);
  const airplanesRef = useRef<{x: number, y: number, speed: number, altitude: number}[]>([]);
  const rainRef = useRef<{x: number, y: number, length: number, speed: number}[]>([]);
  const lightningRef = useRef<{active: boolean, x: number, opacity: number}>({active: false, x: 0, opacity: 0});
  const imageCache = useRef<Map<string, HTMLImageElement>>(new Map());

  // --- URL PARAMS FOR EMBED MODE ---
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mode') === 'embed') {
      setEmbedMode(true);
      setIsSimulating(true);
      setAutoCycle(true);
    }
    if (params.get('bg') === 'transparent') {
      setTransparentBg(true);
      setBackgroundColor('transparent');
    }
    if (params.get('view') === '3d') {
      setViewMode('3d');
    }
  }, []);

  // --- SYNC REF ---
  useEffect(() => {
      nodesRef.current = nodes;
  }, [nodes]);

  // --- INIT ---

  useEffect(() => {
    if (containerRef.current) {
        setOffset({ x: containerRef.current.clientWidth / 2, y: 150 });
        for(let i=0; i<6; i++) {
            const puffs = []; for(let j=0; j<5; j++) puffs.push({x:(Math.random()-0.5)*100, y:(Math.random()-0.5)*40, r:30+Math.random()*40, alpha:0.4+Math.random()*0.4});
            cloudsRef.current.push({x:Math.random()*2000, y:Math.random()*300, speed:0.2+Math.random()*0.4, puffs});
        }
        for(let i=0; i<5; i++) birdsRef.current.push({x:Math.random()*1000, y:100 + Math.random()*200, speedX: 1 + Math.random(), speedY: (Math.random()-0.5)*0.5, flap: 0});
        for(let i=0; i<150; i++) rainRef.current.push({x:Math.random()*2000, y:Math.random()*1000, length:15+Math.random()*25, speed:20+Math.random()*15});
        setGraphHistory(new Array(60).fill({production: 0, consumption: 0, batteryLevel: 50}));
    }

    const saved = localStorage.getItem('energyFlowState_sandbox');
    if (saved) {
        try {
            const parsed = JSON.parse(saved);
            if (parsed.nodes) {
              const loadedNodes = parsed.nodes.map((n:any) => ({...n, animScale: 1}));
              setNodes(loadedNodes);
              // Store initial positions
              loadedNodes.forEach((n: Node) => {
                initialPositionsRef.current.set(n.id, { x: n.x, y: n.y });
              });
            }
            if (parsed.connections) {
              // Migrate old connections to new format with default particleSize of 2
              setConnections(parsed.connections.map((c: any) => ({
                ...c,
                lineType: c.lineType || 'curved',
                particleColor: c.particleColor || '#ffffff',
                particleSize: c.particleSize ?? 2,
              })));
            }
            if (parsed.library) setLibrary(parsed.library);
            if (parsed.backgroundColor) setBackgroundColor(parsed.backgroundColor);
            if (parsed.showGrid !== undefined) setShowGrid(parsed.showGrid);
            if (parsed.weatherValue !== undefined) setWeatherValue(parsed.weatherValue);
            if (parsed.timeOfDay !== undefined) setTimeOfDay(parsed.timeOfDay);
            if (parsed.backgroundSrc) {
                setBackgroundSrc(parsed.backgroundSrc);
                const img = new Image(); img.src = parsed.backgroundSrc; img.onload = () => setBackgroundImg(img);
            }
        } catch (e) { console.error(e); }
    }
  }, []);

  const quotaErrorShownRef = useRef(false);

  useEffect(() => {
      try {
        const stateToSave: AppState = { nodes, connections, library, backgroundSrc, backgroundColor, showGrid, weatherValue, timeOfDay, version: 5 };
        localStorage.setItem('energyFlowState_sandbox', JSON.stringify(stateToSave));
        quotaErrorShownRef.current = false;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
          if (!quotaErrorShownRef.current) {
            console.warn('[EnergyFlow] localStorage quota exceeded');
            toast.error('Opslag vol! Oude afbeeldingen worden niet opgeslagen.');
            quotaErrorShownRef.current = true;
          }
        } else {
          console.error('[EnergyFlow] Error saving state:', error);
        }
      }
  }, [nodes, connections, library, backgroundSrc, backgroundColor, showGrid, weatherValue, timeOfDay]);

  // --- SIMULATION LOGIC ---

  useEffect(() => {
    const animInterval = setInterval(() => {
        setNodes(prev => prev.map(n => {
            let newScale = n.animScale !== undefined ? n.animScale : 0;
            if (newScale < 1) newScale += (1 - newScale) * 0.2; 
            
            let pulse = 0;
            if (isSimulating && n.currentValue && n.currentValue > 0) {
                const phase = (n.pulsePhase || 0) + 0.1;
                pulse = Math.sin(phase) * 0.03; 
                return { ...n, animScale: newScale, pulsePhase: phase, _displayScale: 1 + pulse };
            }
            return { ...n, animScale: newScale, _displayScale: newScale };
        }));
    }, 30);
    return () => clearInterval(animInterval);
  }, [isSimulating]);

  // Auto day/night cycle (independent of simulation)
  useEffect(() => {
    if (!autoDayNight) return;
    
    const interval = setInterval(() => {
      setTimeOfDay(prev => (prev + 0.03) % 24);
    }, 50);
    
    return () => clearInterval(interval);
  }, [autoDayNight]);

  useEffect(() => {
    if (!isSimulating) return;
    
    const interval = setInterval(() => {
        if (autoCycle) {
            setTimeOfDay(prev => (prev + 0.05) % 24);
        }

        let totalProd = 0;
        let totalCons = 0;
        let avgBattery = 0;
        let batteryCount = 0;

        const sunFactor = Math.max(0, Math.sin((timeOfDay - 6) * (Math.PI / 12))); 
        const isDark = timeOfDay < 6 || timeOfDay > 20; 
        
        let weatherFactor = 1.0;
        if (weatherValue > 30) weatherFactor = 0.6; 
        if (weatherValue > 70) weatherFactor = 0.2; 

        const currentNodes = nodesRef.current;
        const nextNodes = currentNodes.map(n => {
            if (!n.baseValue) return n;
            let val = n.baseValue + (Math.random() - 0.5) * (n.baseValue * 0.05); 
            
            if (n.type === 'solar') {
                val = val * sunFactor * weatherFactor;
                totalProd += val;
            }
            if (n.type === 'house' || n.type === 'heatpump') {
                if (isDark) val = val * 0.8; else val = val * 1.2; 
                totalCons += val;
            }
            if (n.type === 'ev_charger') {
                if (isDark) val = val * 1.5; else val = val * 0.2;
                totalCons += val;
            }

            let newCharge = n.batteryCharge || 50;
            let status: 'ok' | 'warning' | 'error' = 'ok';

            if (n.type === 'battery') {
                batteryCount++;
                const netEnergy = totalProd - totalCons;
                if (netEnergy > 0) newCharge = Math.min(100, newCharge + (netEnergy * 0.05));
                else newCharge = Math.max(0, newCharge - (Math.abs(netEnergy) * 0.05));
                avgBattery += newCharge;
                if (newCharge < 10) status = 'error'; else if (newCharge < 30) status = 'warning';
            }

            return { ...n, currentValue: Math.abs(val), batteryCharge: newCharge, status };
        });

        setNodes(nextNodes);

        setGraphHistory(prev => {
            const currentBattery = batteryCount > 0 ? avgBattery / batteryCount : 50;
            const next = [...prev.slice(1), { production: totalProd, consumption: totalCons, batteryLevel: currentBattery }];
            return next;
        });
        
        setConnections(prev => prev.map(c => {
            const fromNode = nextNodes.find(n => n.id === c.fromId);
            if (!fromNode) return c;
            const val = fromNode.currentValue || 0;
            const intensity = Math.min(1, val / 20); 
            return {
                ...c,
                speed: 1 + intensity * 6,
                width: 2 + intensity * 6
            };
        }));

        setKpiData(prev => ({
            selfSufficiency: totalCons > 0 ? Math.min(100, (totalProd / totalCons) * 100) : 100,
            costSaved: prev.costSaved + (totalProd * 0.005),
            co2Saved: prev.co2Saved + (totalProd * 0.002)
        }));

    }, 100); 
    return () => clearInterval(interval);
  }, [isSimulating, autoCycle, timeOfDay, weatherValue]);

  // --- DRAWING ---

  const drawAsset = (ctx: CanvasRenderingContext2D, node: Node, shadowOffset: {x: number, y: number}) => {
    const { x: sx, y: sy } = isoToScreen(node.x, node.y, zoom, offset);
    const z = zoom;
    
    // Get the scale factor - now applies to ALL assets
    const nodeScale = node.scale ?? ASSET_DEFAULTS[node.type]?.defaultScale ?? 1;
    
    if (hoveredNodeId === node.id) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.beginPath();
        ctx.ellipse(sx, sy + (16*z), (40*z), (40*z)*0.6, 0, 0, Math.PI*2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.stroke();
    }

    const animScale = (node as any)._displayScale || 1;
    const totalScale = animScale * nodeScale;
    ctx.save(); ctx.translate(sx, sy); ctx.scale(totalScale, totalScale); ctx.translate(-sx, -sy);

    ctx.fillStyle = 'rgba(0,0,0,0.3)'; ctx.beginPath(); ctx.ellipse(sx + shadowOffset.x, sy + (32*z)/2 + shadowOffset.y, (32*z)/2, (16*z)/2, 0, 0, Math.PI*2); ctx.fill();

    let spriteUrl = node.customImageSrc;
    
    // Check if it's a GLTF model - don't try to load as 2D image
    if (spriteUrl && (
      spriteUrl.startsWith('gltf:') || 
      spriteUrl.startsWith('data:model/gltf') ||
      spriteUrl.startsWith('data:application/octet-stream')
    )) {
      spriteUrl = null; // Fallback to standard 2D asset rendering
    }
    
    if (!spriteUrl && SPRITE_CONFIG[node.type]) spriteUrl = SPRITE_CONFIG[node.type];

    if (spriteUrl) {
        let img = imageCache.current.get(spriteUrl);
        if (img === undefined) { 
            // First time loading this image
            const newImg = new Image(); 
            newImg.onload = () => {
                console.log('[EnergyFlow] Image loaded successfully:', spriteUrl?.substring(0, 50) + '...');
            };
            newImg.onerror = (err) => {
                console.error('[EnergyFlow] Image load failed:', spriteUrl?.substring(0, 50) + '...', err);
                // Mark as failed in cache so we don't keep retrying
                imageCache.current.set(spriteUrl!, null as any);
            };
            newImg.src = spriteUrl; 
            imageCache.current.set(spriteUrl, newImg);
            img = newImg;
        }
        
        // Check if image loaded successfully, failed, or still loading
        if (img === null) {
            // Image failed to load - show error indicator
            ctx.fillStyle = '#ef4444'; 
            ctx.font = `bold ${12*z}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText("❌", sx, sy - 10*z);
            ctx.font = `${9*z}px sans-serif`;
            ctx.fillStyle = '#94a3b8';
            ctx.fillText("Laden mislukt", sx, sy + 5*z);
        } else if (img.complete && img.naturalWidth > 0) {
            ctx.save();
            ctx.globalCompositeOperation = 'source-over';
            const aspect = img.width / img.height;
            const h = 80 * z; const w = h * aspect;
            // Fixed: bottom of image aligns with grid surface
            ctx.drawImage(img, sx - w/2, sy - h + (32*z), w, h);
            ctx.restore();
        } else { 
            // Still loading
            ctx.fillStyle = '#64748b'; 
            ctx.font = `${12*z}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText("⏳", sx, sy); 
        }
    } else {
        const sunFactor = Math.max(0, Math.sin((timeOfDay - 6) * (Math.PI / 12)));
        const isSunny = weatherValue < 30 && sunFactor > 0.5;
        
        switch(node.type) {
            case 'solar':
                drawIsoPrism(ctx, sx, sy, 3*z, 3*z, 10*z, '#64748b', '#475569', '#334155');
                const panelColor = sunFactor < 0.2 ? '#0f172a' : (weatherValue > 70 ? '#1e3a8a' : '#2563eb');
                ctx.fillStyle = panelColor;
                ctx.beginPath(); ctx.moveTo(sx, sy-25*z); ctx.lineTo(sx+35*z, sy-15*z); ctx.lineTo(sx, sy+5*z); ctx.lineTo(sx-35*z, sy-5*z); ctx.fill();
                if (isSunny) { 
                    ctx.save(); ctx.globalCompositeOperation = 'overlay';
                    const grad = ctx.createLinearGradient(sx-20*z, sy-30*z, sx, sy);
                    grad.addColorStop(0, 'rgba(255,255,255,0.6)'); grad.addColorStop(1, 'rgba(255,255,255,0)');
                    ctx.fillStyle = grad; ctx.fill(); ctx.restore();
                }
                ctx.strokeStyle = 'rgba(255,255,255,0.1)'; ctx.beginPath(); ctx.moveTo(sx, sy-25*z); ctx.lineTo(sx, sy+5*z); ctx.stroke();
                break;
            case 'battery': 
                drawIsoPrism(ctx, sx, sy, 18*z, 18*z, 45*z, '#f8fafc', '#e2e8f0', '#cbd5e1');
                ctx.fillStyle = '#94a3b8'; ctx.beginPath(); ctx.moveTo(sx-18*z, sy-36*z); ctx.lineTo(sx-6*z, sy-30*z); ctx.lineTo(sx-6*z, sy+15*z); ctx.lineTo(sx-18*z, sy+9*z); ctx.fill();
                const charge = node.batteryCharge || 50;
                const barHeight = 35 * z;
                const fillHeight = barHeight * (charge / 100);
                ctx.fillStyle = '#334155'; ctx.fillRect(sx+2*z, sy-35*z, 6*z, barHeight);
                ctx.fillStyle = charge > 20 ? '#22c55e' : (charge < 10 ? '#ef4444' : '#eab308'); 
                ctx.fillRect(sx+2*z, sy-35*z + (barHeight - fillHeight), 6*z, fillHeight);
                break;
            case 'grid':
                drawIsoPrism(ctx, sx, sy, 6*z, 6*z, 60*z, '#64748b', '#475569', '#334155');
                ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 1*z; ctx.beginPath(); ctx.moveTo(sx-20*z, sy-40*z); ctx.lineTo(sx+20*z, sy-40*z); ctx.stroke();
                break;
            case 'house': 
                // 2x4 ratio building (width=20, length=40, height=25)
                drawIsoPrism(ctx, sx, sy, 20*z, 40*z, 25*z, '#f1f5f9', '#cbd5e1', '#64748b');
                // Door on front face
                ctx.fillStyle = '#d97706'; 
                ctx.beginPath(); 
                ctx.moveTo(sx, sy+15*z); 
                ctx.lineTo(sx+15*z, sy+7*z); 
                ctx.lineTo(sx+15*z, sy+17*z); 
                ctx.lineTo(sx, sy+25*z); 
                ctx.fill(); 
                // Windows with light effect
                const lightsOn = sunFactor < 0.3 || weatherValue > 80;
                ctx.fillStyle = lightsOn ? '#fef3c7' : '#334155'; 
                if (lightsOn) { ctx.shadowColor = '#fef3c7'; ctx.shadowBlur = 15; }
                // Window 1
                ctx.beginPath(); 
                ctx.moveTo(sx+5*z, sy+5*z); 
                ctx.lineTo(sx+10*z, sy+2.5*z); 
                ctx.lineTo(sx+10*z, sy+8*z); 
                ctx.lineTo(sx+5*z, sy+10.5*z); 
                ctx.fill();
                // Window 2
                ctx.beginPath(); 
                ctx.moveTo(sx+5*z, sy-5*z); 
                ctx.lineTo(sx+10*z, sy-7.5*z); 
                ctx.lineTo(sx+10*z, sy-2*z); 
                ctx.lineTo(sx+5*z, sy+0.5*z); 
                ctx.fill();
                ctx.shadowBlur = 0;
                break;
            case 'ev_charger':
                drawIsoPrism(ctx, sx, sy, 5*z, 5*z, 20*z, '#334155', '#1e293b', '#0f172a');
                ctx.fillStyle = isSimulating && sunFactor < 0.2 ? '#10b981' : '#ec4899'; ctx.beginPath(); ctx.arc(sx, sy-15*z, 2*z, 0, Math.PI*2); ctx.fill();
                break;
            case 'heatpump':
                drawIsoPrism(ctx, sx, sy, 12*z, 12*z, 15*z, '#e2e8f0', '#94a3b8', '#64748b');
                ctx.fillStyle = '#334155'; ctx.beginPath(); ctx.arc(sx-5*z, sy-5*z, 4*z, 0, Math.PI*2); ctx.fill();
                break;
            case 'custom':
                 drawIsoPrism(ctx, sx, sy, 10*z, 10*z, 10*z, '#eee', '#ddd', '#ccc');
                 break;
        }
    }
    
    ctx.restore();

    if (isSimulating && node.status === 'error') {
        if (Math.floor(Date.now() / 500) % 2 === 0) {
            ctx.fillStyle = '#ef4444';
            ctx.beginPath(); ctx.moveTo(sx, sy - 80*z); ctx.lineTo(sx+10*z, sy - 65*z); ctx.lineTo(sx-10*z, sy - 65*z); ctx.fill();
            ctx.fillStyle = '#fff'; ctx.font = `bold ${10*z}px sans-serif`; ctx.textAlign='center'; ctx.fillText('!', sx, sy - 68*z);
        }
    }

    // Show link icon if node has clickUrl (in embed mode or when selected)
    if (node.clickUrl && (embedMode || selectedEntity?.id === node.id)) {
        ctx.fillStyle = 'rgba(59, 130, 246, 0.9)';
        ctx.beginPath();
        ctx.arc(sx + 25*z, sy - 50*z, 8*z, 0, Math.PI*2);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = `${8*z}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillText('🔗', sx + 25*z, sy - 47*z);
    }

    if (node.label || isSimulating) {
        ctx.font = `bold ${11*z}px Inter, sans-serif`;
        let text = node.label || '';
        if (isSimulating && node.baseValue !== undefined) {
             const val = node.currentValue !== undefined ? node.currentValue : node.baseValue;
             text = `${val.toFixed(1)} kW`;
        }
        const tw = ctx.measureText(text).width;
        ctx.fillStyle = isSimulating ? 'rgba(30, 41, 59, 0.95)' : 'rgba(15, 23, 42, 0.8)';
        if (isSimulating) {
             if (node.type === 'solar') ctx.fillStyle = 'rgba(21, 128, 61, 0.9)';
             if (node.type === 'house' || node.type === 'ev_charger') ctx.fillStyle = 'rgba(185, 28, 28, 0.9)';
        }
        ctx.beginPath(); ctx.roundRect(sx - tw/2 - 8*z, sy - 60*z - (node.type==='grid'?20*z:0), tw + 16*z, 22*z, 4); ctx.fill();
        ctx.fillStyle = '#fff'; ctx.textAlign = 'center'; ctx.fillText(text, sx, sy - 60*z - (node.type==='grid'?20*z:0) + 15*z);
    }
  };

  // Draw connection with line type support
  const drawConnection = (ctx: CanvasRenderingContext2D, c: Connection, time: number) => {
    const f = nodes.find(n => n.id === c.fromId);
    const t = nodes.find(n => n.id === c.toId);
    if (!f || !t) return;
    
    let isActive = true;
    const isDark = timeOfDay < 6 || timeOfDay > 20;
    if (isDark && !c.activeInNight) isActive = false;
    if (!isDark && !c.activeInDay) isActive = false;
    
    const s = isoToScreen(f.x, f.y, zoom, offset);
    const e = isoToScreen(t.x, t.y, zoom, offset);
    const p0 = { x: s.x, y: s.y + 16*zoom };
    const p2 = { x: e.x, y: e.y + 16*zoom };
    const p1 = { x: (s.x+e.x)/2, y: (p0.y+p2.y)/2 - 40*zoom };
    
    const lineType = c.lineType || 'curved';
    
    // Draw selection highlight
    if (selectedEntity?.id === c.id) {
      ctx.shadowColor = '#fff';
      ctx.shadowBlur = 10;
      ctx.strokeStyle = 'rgba(255,255,255,0.8)';
      ctx.lineWidth = (c.width + 6) * zoom;
      ctx.beginPath();
      
      if (lineType === 'straight') {
        ctx.moveTo(p0.x, p0.y);
        ctx.lineTo(p2.x, p2.y);
      } else if (lineType === 'orthogonal') {
        ctx.moveTo(p0.x, p0.y);
        ctx.lineTo(p2.x, p0.y);
        ctx.lineTo(p2.x, p2.y);
      } else {
        ctx.moveTo(p0.x, p0.y);
        ctx.quadraticCurveTo(p1.x, p1.y, p2.x, p2.y);
      }
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
    
    // Draw main line
    ctx.beginPath();
    if (lineType === 'straight') {
      ctx.moveTo(p0.x, p0.y);
      ctx.lineTo(p2.x, p2.y);
    } else if (lineType === 'orthogonal') {
      ctx.moveTo(p0.x, p0.y);
      ctx.lineTo(p2.x, p0.y);
      ctx.lineTo(p2.x, p2.y);
    } else {
      ctx.moveTo(p0.x, p0.y);
      ctx.quadraticCurveTo(p1.x, p1.y, p2.x, p2.y);
    }
    
    ctx.strokeStyle = isActive ? c.color : '#475569';
    
    if (isActive && isSimulating && c.width > 4) {
      ctx.shadowColor = c.color;
      ctx.shadowBlur = c.width * 2;
      ctx.globalCompositeOperation = 'lighter';
    }
    
    ctx.globalAlpha = isActive ? 0.3 : 0.1;
    ctx.lineWidth = c.width * zoom;
    ctx.stroke();
    ctx.globalAlpha = 1.0;
    ctx.shadowBlur = 0;
    ctx.globalCompositeOperation = 'source-over';
    
    // Draw particles
    if (isActive) {
      const particleColor = c.particleColor || '#ffffff';
      const particleSize = c.particleSize || 5;
      
      for (let i = 0; i < 6; i++) {
        let tVal = (time * (c.speed * 0.2) + (i / 6)) % 1;
        if (c.direction === 'reverse') tVal = 1 - tVal;
        
        let pos;
        if (lineType === 'curved') {
          pos = getBezierPoint(tVal, p0, p1, p2);
        } else {
          pos = getLinePoint(tVal, p0, p2, lineType);
        }
        
        ctx.fillStyle = particleColor;
        ctx.shadowColor = c.color;
        ctx.shadowBlur = 10;
        
        if (c.shape === 'square') {
          ctx.fillRect(pos.x - particleSize * zoom, pos.y - particleSize * zoom, particleSize * 2 * zoom, particleSize * 2 * zoom);
        } else {
          ctx.beginPath();
          ctx.arc(pos.x, pos.y, c.shape === 'circle' ? particleSize * zoom : (particleSize * 0.6) * zoom, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.shadowBlur = 0;
      }
    }
    
    // Draw sparks for high load
    if (isActive && isSimulating && (c.width > 6) && Math.random() < 0.05) {
      let tRand = Math.random();
      let pos;
      if (lineType === 'curved') {
        pos = getBezierPoint(tRand, p0, p1, p2);
      } else {
        pos = getLinePoint(tRand, p0, p2, lineType);
      }
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      ctx.lineTo(pos.x + (Math.random() - 0.5) * 10, pos.y + (Math.random() - 0.5) * 10);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true }); if (!ctx) return;
    let animId: number;

    const render = () => {
      // Handle transparent background
      if (backgroundColor === 'transparent' || transparentBg) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      } else if (lightningRef.current.active) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          lightningRef.current.opacity -= 0.1;
          if (lightningRef.current.opacity <= 0) lightningRef.current.active = false;
      } else if (backgroundColor === 'auto') {
          const { top, bottom } = getSkyGradient(timeOfDay);
          const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
          if (weatherValue > 60) {
              grad.addColorStop(0, '#334155');
              grad.addColorStop(1, '#64748b');
          } else {
              grad.addColorStop(0, top);
              grad.addColorStop(1, bottom);
          }
          ctx.fillStyle = grad;
          ctx.fillRect(0,0, canvas.width, canvas.height);
      } else {
          ctx.fillStyle = backgroundColor;
          ctx.fillRect(0,0, canvas.width, canvas.height);
      }

      const centerX = canvas.width / 2; const centerY = canvas.height * 0.6; const radius = canvas.width * 0.35;
      let sunAngle = 0;
      if (timeOfDay >= 6 && timeOfDay <= 18) sunAngle = Math.PI - ((timeOfDay - 6) / 12) * Math.PI; else sunAngle = -1;
      const sunX = centerX + Math.cos(sunAngle) * radius;
      // Ensure sun stays above grid - minimum height at canvas.height * 0.15
      const rawSunY = centerY - Math.sin(sunAngle) * radius;
      const sunY = Math.min(rawSunY, canvas.height * 0.35);
      
      let shadowOffset = { x: 0, y: 0 };
      if (timeOfDay > 5.5 && timeOfDay < 18.5) {
          const sunHeight = Math.sin(sunAngle); const shadowLen = (1 - sunHeight) * 40 * zoom; const shadowDir = -Math.cos(sunAngle); 
          shadowOffset = { x: shadowDir * shadowLen, y: 5 * zoom }; 
      }

      // Draw stars only if not transparent
      if (backgroundColor !== 'transparent' && !transparentBg) {
          if ((backgroundColor === 'auto' && (timeOfDay < 5 || timeOfDay > 20))) {
              const seededRandom = (seed: number) => {
                  const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
                  return x - Math.floor(x);
              };
              ctx.fillStyle = '#fff';
              for (let i = 0; i < 60; i++) {
                  const sx = seededRandom(i * 1.1) * canvas.width;
                  const sy = seededRandom(i * 2.3) * (canvas.height * 0.45);
                  const twinkle = Math.sin(Date.now() / 2000 + i * 0.7) * 0.3 + 0.7;
                  ctx.globalAlpha = twinkle;
                  const size = 1 + seededRandom(i * 3.7) * 1.5;
                  ctx.beginPath();
                  for (let j = 0; j < 4; j++) {
                      const angle = (j * Math.PI) / 2;
                      const outerX = sx + Math.cos(angle) * size;
                      const outerY = sy + Math.sin(angle) * size;
                      if (j === 0) ctx.moveTo(outerX, outerY);
                      else ctx.lineTo(outerX, outerY);
                      const innerAngle = angle + Math.PI / 4;
                      const innerX = sx + Math.cos(innerAngle) * (size * 0.3);
                      const innerY = sy + Math.sin(innerAngle) * (size * 0.3);
                      ctx.lineTo(innerX, innerY);
                  }
                  ctx.closePath();
                  ctx.fill();
              }
              ctx.globalAlpha = 1.0;
          }
      }

      // Draw sun only if not transparent
      if (backgroundColor !== 'transparent' && !transparentBg) {
          if (timeOfDay > 5.5 && timeOfDay < 18.5 && weatherValue < 80) {
              const sunGrad = ctx.createRadialGradient(sunX, sunY, 10, sunX, sunY, 120);
              sunGrad.addColorStop(0, '#fff'); sunGrad.addColorStop(0.1, '#fef08a'); sunGrad.addColorStop(0.4, 'rgba(253, 224, 71, 0.5)'); sunGrad.addColorStop(1, 'rgba(253, 224, 71, 0)');
              ctx.fillStyle = sunGrad; ctx.beginPath(); ctx.arc(sunX, sunY, 150, 0, Math.PI*2); ctx.fill();
              
              if (weatherValue < 40) {
                  ctx.save(); ctx.translate(sunX, sunY); ctx.rotate(Date.now()/8000);
                  ctx.globalCompositeOperation = 'screen'; ctx.fillStyle = 'rgba(255,255,255,0.05)';
                  for(let r=0; r<8; r++) { ctx.rotate(Math.PI/4); ctx.beginPath(); ctx.moveTo(0,0); ctx.lineTo(60, 600); ctx.lineTo(-60, 600); ctx.fill(); }
                  ctx.restore();
              }
          }
      }

      if (backgroundImg) {
          const baseSize = GRID_SIZE * TILE_WIDTH * zoom * 1.5 * bgOverlayScale;
          const dw = baseSize; 
          const dh = baseSize / (backgroundImg.width / backgroundImg.height);
          ctx.globalAlpha = bgOverlayOpacity; 
          ctx.drawImage(
            backgroundImg, 
            offset.x - dw/2 + bgOverlayOffset.x * zoom, 
            offset.y - dh/4 + bgOverlayOffset.y * zoom, 
            dw, 
            dh
          ); 
          ctx.globalAlpha = 1.0;
      }

      if (showGrid && !embedMode) {
          ctx.strokeStyle = (timeOfDay < 6 || timeOfDay > 20 || weatherValue > 70) ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)'; 
          ctx.lineWidth = 1 * zoom;
          for (let x = 0; x < GRID_SIZE; x++) for (let y = 0; y < GRID_SIZE; y++) {
              const { x: sx, y: sy } = isoToScreen(x, y, zoom, offset);
              ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(sx+(32*zoom), sy+(16*zoom)); ctx.lineTo(sx, sy+(32*zoom)); ctx.lineTo(sx-(32*zoom), sy+(16*zoom)); ctx.closePath();
              if (hoverPos && hoverPos.x === x && hoverPos.y === y) { ctx.fillStyle = 'rgba(255,255,255,0.1)'; ctx.fill(); }
              ctx.stroke();
          }
      }

      const time = Date.now() / 1000;

      // CONNECTIONS - use new drawing function
      connections.forEach(c => drawConnection(ctx, c, time));

      if (selectedTool === 'connect' && connectionStart && hoverPos) {
          const sn = nodes.find(n => n.id === connectionStart);
          if (sn) {
              const s = isoToScreen(sn.x, sn.y, zoom, offset); const e = isoToScreen(hoverPos.x, hoverPos.y, zoom, offset);
              ctx.beginPath(); ctx.moveTo(s.x, s.y+16*zoom); ctx.lineTo(e.x, e.y+16*zoom); ctx.strokeStyle = '#fbbf24'; ctx.lineWidth = 2*zoom; ctx.setLineDash([8*zoom, 8*zoom]); ctx.stroke(); ctx.setLineDash([]);
          }
      }

      [...nodes].sort((a,b)=>(a.x+a.y)-(b.x+b.y)).forEach(n => {
          drawAsset(ctx, n, shadowOffset); 
          // Single selection highlight (blue)
          if (selectedEntity?.id === n.id) {
              const { x: sx, y: sy } = isoToScreen(n.x, n.y, zoom, offset);
              ctx.strokeStyle = '#38bdf8'; ctx.lineWidth = 3; ctx.beginPath(); ctx.ellipse(sx, sy + (16*zoom), (32*zoom)/1.5, (16*zoom)/1.5, 0, 0, Math.PI*2); ctx.stroke();
          }
          // Multi-selection highlight (green) - for all nodes in selectedNodeIds
          if (selectedNodeIds.has(n.id) && selectedEntity?.id !== n.id) {
              const { x: sx, y: sy } = isoToScreen(n.x, n.y, zoom, offset);
              ctx.strokeStyle = '#22c55e'; ctx.lineWidth = 2; ctx.setLineDash([4, 4]); ctx.beginPath(); ctx.ellipse(sx, sy + (16*zoom), (35*zoom)/1.5, (18*zoom)/1.5, 0, 0, Math.PI*2); ctx.stroke(); ctx.setLineDash([]);
          }
      });

      // WEATHER - only draw if not transparent
      if (backgroundColor !== 'transparent' && !transparentBg) {
          if (weatherValue > 20) {
              cloudsRef.current.forEach(c => { c.x += c.speed; if(c.x > canvas.width + 200) c.x = -200; ctx.fillStyle = weatherValue > 60 ? 'rgba(71,85,105,0.9)' : 'rgba(255,255,255,0.7)'; c.puffs.forEach(p => { ctx.beginPath(); ctx.arc(c.x + p.x, c.y + p.y, p.r * (0.8 + weatherValue/200), 0, Math.PI*2); ctx.globalAlpha = p.alpha; ctx.fill(); }); ctx.globalAlpha = 1.0; });
          }
          if (weatherValue > 60) {
              rainRef.current.forEach(r => { r.y += r.speed; if(r.y > canvas.height) { r.y = -r.length; r.x = Math.random() * canvas.width; } ctx.strokeStyle = 'rgba(147, 197, 253, 0.5)'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(r.x, r.y); ctx.lineTo(r.x + 3, r.y + r.length); ctx.stroke(); });
              if (weatherValue > 85 && Math.random() < 0.002 && !lightningRef.current.active) { lightningRef.current = { active: true, x: Math.random() * canvas.width, opacity: 1 }; }
          }
          if (timeOfDay > 6.5 && timeOfDay < 17 && weatherValue < 40) {
              birdsRef.current.forEach(b => { b.x += b.speedX; b.y += b.speedY; b.flap = (b.flap + 0.3) % (Math.PI*2); if(b.x > canvas.width+50) { b.x = -50; b.y = 80 + Math.random()*200; } ctx.strokeStyle = '#1e293b'; ctx.lineWidth = 2; ctx.beginPath(); const wing = Math.sin(b.flap)*8; ctx.moveTo(b.x-12, b.y + wing); ctx.quadraticCurveTo(b.x-4, b.y-4, b.x, b.y); ctx.quadraticCurveTo(b.x+4, b.y-4, b.x+12, b.y + wing); ctx.stroke(); });
          }
      }

      animId = requestAnimationFrame(render);
    };

    render();
    return () => cancelAnimationFrame(animId);
  }, [nodes, connections, backgroundColor, transparentBg, backgroundImg, showGrid, zoom, offset, hoverPos, hoveredNodeId, selectedEntity, selectedNodeIds, timeOfDay, weatherValue, isSimulating, connectionStart, selectedTool, embedMode, bgOverlayOffset, bgOverlayScale, bgOverlayOpacity]);

  // --- GRAPH ---
  useEffect(() => {
    const canvas = graphCanvasRef.current; if (!canvas || !isSimulating) return;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    
    ctx.fillStyle = 'rgba(30, 41, 59, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const data = graphHistory;
    if (data.length < 2) return;

    const maxVal = Math.max(20, ...data.flatMap(d => [d.production, d.consumption]));
    const scaleY = (v: number) => canvas.height - (v / maxVal) * (canvas.height - 20) - 10;
    const scaleX = (i: number) => (i / (data.length - 1)) * canvas.width;

    // Production area
    ctx.beginPath();
    ctx.moveTo(0, canvas.height);
    data.forEach((d, i) => ctx.lineTo(scaleX(i), scaleY(d.production)));
    ctx.lineTo(canvas.width, canvas.height);
    ctx.closePath();
    const prodGrad = ctx.createLinearGradient(0, 0, 0, canvas.height);
    prodGrad.addColorStop(0, 'rgba(34, 197, 94, 0.5)');
    prodGrad.addColorStop(1, 'rgba(34, 197, 94, 0.05)');
    ctx.fillStyle = prodGrad;
    ctx.fill();

    // Consumption area
    ctx.beginPath();
    ctx.moveTo(0, canvas.height);
    data.forEach((d, i) => ctx.lineTo(scaleX(i), scaleY(d.consumption)));
    ctx.lineTo(canvas.width, canvas.height);
    ctx.closePath();
    const consGrad = ctx.createLinearGradient(0, 0, 0, canvas.height);
    consGrad.addColorStop(0, 'rgba(239, 68, 68, 0.5)');
    consGrad.addColorStop(1, 'rgba(239, 68, 68, 0.05)');
    ctx.fillStyle = consGrad;
    ctx.fill();

    // Production line
    ctx.beginPath();
    data.forEach((d, i) => { if (i === 0) ctx.moveTo(scaleX(i), scaleY(d.production)); else ctx.lineTo(scaleX(i), scaleY(d.production)); });
    ctx.strokeStyle = '#22c55e';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Consumption line
    ctx.beginPath();
    data.forEach((d, i) => { if (i === 0) ctx.moveTo(scaleX(i), scaleY(d.consumption)); else ctx.lineTo(scaleX(i), scaleY(d.consumption)); });
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 2;
    ctx.stroke();

  }, [graphHistory, isSimulating]);

  // --- RESIZE ---
  useEffect(() => {
    const handleResize = () => { if (canvasRef.current && containerRef.current) { canvasRef.current.width = containerRef.current.clientWidth; canvasRef.current.height = containerRef.current.clientHeight; } };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- KEYBOARD SHORTCUTS ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape - close popups and deselect
      if (e.key === 'Escape') {
        setHoveredAssetType(null);
        setIsUploading(false);
        setUploadingAssetType(null);
        setShowColorPicker(false);
        setShowBgOverlayControls(false);
        setShowResetMenu(false);
        setShowCameraMenu(false);
        setShowWeatherControl(false);
        if (selectedNodeIds.size > 0) {
          setSelectedNodeIds(new Set());
        } else {
          setSelectedEntity(null);
        }
      }
      
      // Delete - remove selected entities
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedEntity) {
          deleteSel();
        } else if (selectedNodeIds.size > 0) {
          // Delete all selected nodes
          setNodes(prev => prev.filter(n => !selectedNodeIds.has(n.id)));
          setConnections(prev => prev.filter(c => !selectedNodeIds.has(c.fromId) && !selectedNodeIds.has(c.toId)));
          selectedNodeIds.forEach(id => initialPositionsRef.current.delete(id));
          setSelectedNodeIds(new Set());
          toast.success(`${selectedNodeIds.size} items verwijderd`);
        }
      }
      
      // Select all - Ctrl/Cmd+A
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        setSelectedNodeIds(new Set(nodes.map(n => n.id)));
        toast.info(`${nodes.length} items geselecteerd`);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEntity, selectedNodeIds, nodes]);

  // --- INTERACTION ---

  const handleMouseMove = (e: React.MouseEvent) => {
      // Handle marquee selection
      if (isMarqueeSelecting && marqueeStart) {
        setMarqueeEnd({ x: e.clientX, y: e.clientY });
        return;
      }
      
      // Handle multi-selection drag
      if (isDraggingSelection && dragSelectionStart && selectedNodeIds.size > 0) {
        const r = canvasRef.current?.getBoundingClientRect();
        if (r) {
          const currentIso = screenToIso(e.clientX - r.left, e.clientY - r.top, zoom, offset);
          const startIso = screenToIso(dragSelectionStart.x - r.left, dragSelectionStart.y - r.top, zoom, offset);
          const deltaX = currentIso.x - startIso.x;
          const deltaY = currentIso.y - startIso.y;
          
          if (Math.abs(deltaX) >= 1 || Math.abs(deltaY) >= 1) {
            setNodesWithoutHistory(prev => prev.map(n => {
              if (selectedNodeIds.has(n.id)) {
                const initialPos = initialPositionsRef.current.get(n.id);
                if (initialPos) {
                  return {
                    ...n,
                    x: Math.max(0, Math.min(GRID_SIZE - 1, Math.round(initialPos.x + deltaX))),
                    y: Math.max(0, Math.min(GRID_SIZE - 1, Math.round(initialPos.y + deltaY)))
                  };
                }
              }
              return n;
            }));
          }
        }
        return;
      }
      
      // Handle background overlay pan mode
      if (isDragging && editingBgOverlay && backgroundImg) {
        setBgOverlayOffset(prev => ({
          x: prev.x + (e.clientX - dragStart.x) / zoom,
          y: prev.y + (e.clientY - dragStart.y) / zoom
        }));
        setDragStart({ x: e.clientX, y: e.clientY });
        return;
      }
      
      if (isDragging) { setOffset({ x: offset.x + e.clientX - dragStart.x, y: offset.y + e.clientY - dragStart.y }); setDragStart({ x: e.clientX, y: e.clientY }); return; }
      const r = canvasRef.current?.getBoundingClientRect(); if(!r) return;
      const mouseX = e.clientX - r.left;
      const mouseY = e.clientY - r.top;
      const rawIso = screenToIso(mouseX, mouseY, zoom, offset);
      const snappedIso = getSnappedPosition(rawIso, selectedTool);
      if (snappedIso.x >= 0 && snappedIso.x < GRID_SIZE && snappedIso.y >= 0 && snappedIso.y < GRID_SIZE) { setHoverPos(snappedIso); } else { setHoverPos(null); }

      let foundNodeId: string | null = null;
      let minDist = 1000;
      
      nodesRef.current.forEach(n => {
          const s = isoToScreen(n.x, n.y, zoom, offset);
          const dist = Math.sqrt(Math.pow(mouseX - s.x, 2) + Math.pow(mouseY - s.y, 2));
          if (dist < 30 * zoom && dist < minDist) {
              minDist = dist;
              foundNodeId = n.id;
          }
      });
      
      setHoveredNodeId(foundNodeId);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    // Middle mouse or shift+click for pan
    if (e.button === 1 || (e.button === 0 && e.shiftKey && !hoveredNodeId)) { 
        setIsDragging(true); 
        setDragStart({ x: e.clientX, y: e.clientY }); 
        return; 
    }
    
    // Handle click-through in embed mode
    if (embedMode && hoveredNodeId) {
        const node = nodes.find(n => n.id === hoveredNodeId);
        if (node?.clickUrl) {
            window.open(node.clickUrl, node.clickTarget || '_blank');
            return;
        }
    }
    
    if (hoveredNodeId) {
        // Shift+click for multi-select
        if (e.shiftKey && selectedTool === 'select') {
            setSelectedNodeIds(prev => {
                const newSet = new Set(prev);
                if (newSet.has(hoveredNodeId)) {
                    newSet.delete(hoveredNodeId);
                } else {
                    newSet.add(hoveredNodeId);
                }
                return newSet;
            });
            return;
        }
        
        if (selectedTool === 'connect') {
            if (!connectionStart) setConnectionStart(hoveredNodeId);
            else if (hoveredNodeId !== connectionStart) {
                setConnections(prev => [...prev, {
                    id: Math.random().toString(36).substr(2,9),
                    fromId: connectionStart,
                    toId: hoveredNodeId!,
                    color:'#60a5fa', speed:2, width:3, direction:'forward', shape:'dash',
                    activeInDay: true, activeInNight: true,
                    lineType: 'curved',
                    particleColor: '#ffffff',
                    particleSize: 2
                }]);
                setConnectionStart(null);
                setSelectedTool('select');
            }
        } else {
            // Check if clicking on an already selected node for multi-drag
            if (selectedNodeIds.has(hoveredNodeId) && selectedNodeIds.size > 1 && selectedTool === 'select') {
                // Start multi-drag - store initial positions for all selected nodes
                selectedNodeIds.forEach(id => {
                    const node = nodes.find(n => n.id === id);
                    if (node) {
                        initialPositionsRef.current.set(id, { x: node.x, y: node.y });
                    }
                });
                setIsDraggingSelection(true);
                setDragSelectionStart({ x: e.clientX, y: e.clientY });
                return;
            }
            
            // Regular click - select single node
            setSelectedEntity({ type: 'node', id: hoveredNodeId });
            setSelectedNodeIds(new Set([hoveredNodeId]));
            setConnectionStart(null);
        }
        return;
    }

    if (selectedTool === 'select') {
        const r = canvasRef.current?.getBoundingClientRect();
        if (r) {
            const mx = e.clientX - r.left;
            const my = e.clientY - r.top;
            let bestConn = null;
            let minD = 15 * zoom;
            
            connections.forEach(c => {
                const f = nodesRef.current.find(n => n.id === c.fromId);
                const t = nodesRef.current.find(n => n.id === c.toId);
                if (f && t) {
                    const s = isoToScreen(f.x, f.y, zoom, offset);
                    const ee = isoToScreen(t.x, t.y, zoom, offset);
                    const midX = (s.x + ee.x) / 2;
                    const midY = (s.y + ee.y) / 2 - 20 * zoom;
                    const d = Math.sqrt(Math.pow(mx - midX, 2) + Math.pow(my - midY, 2));
                    if (d < minD) {
                        minD = d;
                        bestConn = c.id;
                    }
                }
            });
            
            if (bestConn) {
                setSelectedEntity({ type: 'connection', id: bestConn });
                setSelectedNodeIds(new Set());
                return;
            }
            
            // Start marquee selection on empty space
            setIsMarqueeSelecting(true);
            setMarqueeStart({ x: e.clientX, y: e.clientY });
            setMarqueeEnd({ x: e.clientX, y: e.clientY });
            return;
        }
        // Click on empty space - deselect all
        setSelectedEntity(null);
        setSelectedNodeIds(new Set());
    }

    if (selectedTool !== 'select' && selectedTool !== 'connect' && hoverPos && !hoveredNodeId) {
        const def = ASSETS.find(a => a.type === selectedTool);
        const assetDef = ASSET_DEFAULTS[selectedTool as AssetType];
        const newNodeId = Math.random().toString(36).substr(2,9);
        
        console.log('[EnergyFlow] Creating node:', {
          id: newNodeId,
          type: selectedTool,
          position: hoverPos,
          hasCustomImage: !!activeImageSrc,
          customImagePreview: activeImageSrc?.substring(0, 50) + '...'
        });
        
        // Determine if activeImageSrc is a GLTF model (prefixed with 'gltf:')
        const isGLTFModel = activeImageSrc?.startsWith('gltf:');
        const gltfUrl = isGLTFModel ? activeImageSrc.substring(5) : undefined;
        const imageUrl = !isGLTFModel ? activeImageSrc : null;
        
        const newNode: Node = {
            id: newNodeId,
            type: selectedTool as AssetType,
            x: hoverPos.x,
            y: hoverPos.y,
            label: '',
            baseValue: def ? def.defaultKw : 0,
            customImageSrc: imageUrl,
            customModelUrl: gltfUrl,
            scale: assetDef?.defaultScale || 1,
            batteryCharge: 50,
            animScale: 0
        };
        
        setNodes(prev => [...prev, newNode]);
        
        // Store initial position
        initialPositionsRef.current.set(newNodeId, { x: hoverPos.x, y: hoverPos.y });
        
        console.log('[EnergyFlow] Node created successfully with customImageSrc:', activeImageSrc ? 'yes' : 'no');
    }
  };

  const handleMouseEnterAsset = (type: AssetType) => { if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current); setHoveredAssetType(type); };
  const handleMouseLeaveAsset = () => { hoverTimeoutRef.current = setTimeout(() => { setHoveredAssetType(null); }, 300); };
  // Helper function to compress images for library storage (preserves transparency)
  const compressImage = (base64: string, maxSize = 200): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ratio = Math.min(maxSize / img.width, maxSize / img.height, 1);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        // Use alpha: true for transparency support
        const ctx = canvas.getContext('2d', { alpha: true });
        if (!ctx) { reject('No canvas context'); return; }
        // Clear canvas with transparent background
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.globalCompositeOperation = 'source-over';
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        // Use PNG format to preserve transparency
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = base64;
    });
  };

  const handleLibraryUpload = (e: React.ChangeEvent<HTMLInputElement>) => { 
    try {
      const type = uploadTypeRef.current || uploadingAssetType; 
      console.log('[EnergyFlow] Library upload triggered, type:', type);
      
      if (!type) {
        console.error('[EnergyFlow] Upload failed: No asset type selected');
        setIsUploading(false);
        setUploadingAssetType(null);
        return; 
      }
      
      const file = e.target.files?.[0]; 
      if (!file) {
        console.error('[EnergyFlow] Upload failed: No file selected');
        setIsUploading(false);
        setUploadingAssetType(null);
        return; 
      }
      
      console.log('[EnergyFlow] Processing file:', file.name, 'size:', file.size, 'type:', file.type);
      
      // Check if GLTF/GLB 3D model
      const isGLTF = file.name.match(/\.(glb|gltf)$/i);
      
      // Validate file type
      if (!file.type.startsWith('image/') && !isGLTF) {
        console.error('[EnergyFlow] Upload failed: File is not an image or 3D model, got:', file.type);
        toast.error('Alleen afbeeldingen (JPG, PNG) of 3D modellen (GLB, GLTF) zijn toegestaan');
        setIsUploading(false);
        setUploadingAssetType(null);
        e.target.value = '';
        return;
      }
      
      // Validate file size (max 5MB)
      const MAX_SIZE = 5 * 1024 * 1024;
      if (file.size > MAX_SIZE) {
        console.error('[EnergyFlow] Upload failed: File too large:', file.size, 'bytes (max 5MB)');
        toast.error('Afbeelding is te groot. Maximum grootte is 5MB.');
        setIsUploading(false);
        setUploadingAssetType(null);
        return;
      }
      
      const reader = new FileReader(); 
      
      reader.onloadend = async () => { 
        const base64 = reader.result as string;
        
        // Handle GLTF/GLB files differently
        if (isGLTF) {
          console.log('[EnergyFlow] 3D model loaded, length:', base64.length, 'chars');
          setLibrary(prev => ({ ...prev, [type]: [...(prev[type] || []), base64] }));
          setSelectedTool(type);
          setActiveImageSrc(base64);
          setIsUploading(false);
          setUploadingAssetType(null);
          toast.success('3D model toegevoegd aan bibliotheek');
          if (libraryInputRef.current) libraryInputRef.current.value = '';
          return;
        }
        
        if (!base64 || !base64.startsWith('data:image')) {
          console.error('[EnergyFlow] Upload failed: Invalid base64 result');
          toast.error('Kon afbeelding niet verwerken.');
          setIsUploading(false);
          setUploadingAssetType(null);
          return;
        }
        
        console.log('[EnergyFlow] File converted to base64, length:', base64.length, 'chars');
        
        const testImg = new Image();
        testImg.onload = async () => {
          console.log('[EnergyFlow] Image validated successfully');
          setLibrary(prev => ({ ...prev, [type]: [...(prev[type] || []), base64] }));
          setSelectedTool(type);
          setActiveImageSrc(base64);
          setIsUploading(false);
          setUploadingAssetType(null);
          toast.success('Afbeelding toegevoegd aan bibliotheek');
          if (libraryInputRef.current) libraryInputRef.current.value = '';
        };
        testImg.onerror = () => {
          toast.error('Afbeelding kon niet worden geladen.');
          setIsUploading(false);
          setUploadingAssetType(null);
          if (libraryInputRef.current) libraryInputRef.current.value = '';
        };
        testImg.src = base64;
      };
      
      reader.onerror = (err) => {
        console.error('[EnergyFlow] FileReader error:', err);
        toast.error('Fout bij het lezen van het bestand. Probeer opnieuw.');
        setIsUploading(false);
        setUploadingAssetType(null);
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('[EnergyFlow] Unexpected error in handleLibraryUpload:', error);
      toast.error('Er ging iets mis bij het uploaden. Probeer opnieuw.');
      setIsUploading(false);
      setUploadingAssetType(null);
      if (libraryInputRef.current) {
        libraryInputRef.current.value = '';
      }
    }
  };
  
  const selectLibraryItem = (type: AssetType, src: string | null) => { 
    console.log('[EnergyFlow] Library item selected:', type, 'image:', src ? 'custom' : 'default');
    setSelectedTool(type); 
    setActiveImageSrc(src); 
    setHoveredAssetType(null); 
  };
  const handleMainToolClick = (type: AssetType) => { 
    setSelectedTool(type); 
    setActiveImageSrc(null); 
    // Also toggle/show the library popup on click for better UX
    if (selectedTool === type) {
      setHoveredAssetType(prev => prev === type ? null : type);
    } else {
      setHoveredAssetType(type);
    }
  };
  const handleBackgroundUpload = (e: React.ChangeEvent<HTMLInputElement>) => { const f = e.target.files?.[0]; if(!f) return; const r = new FileReader(); r.onloadend=()=>{ const src = r.result as string; setBackgroundSrc(src); const img = new Image(); img.src = src; img.onload = () => setBackgroundImg(img); }; r.readAsDataURL(f); };
  const handleJSONExport = () => { const blob = new Blob([JSON.stringify({ nodes, connections, library, backgroundSrc, backgroundColor, showGrid, weatherValue, timeOfDay, version: 5 })], { type: 'application/json' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'energy-flow-sandbox.json'; a.click(); };
  const handleJSONImport = (e: React.ChangeEvent<HTMLInputElement>) => { const f = e.target.files?.[0]; if(!f) return; const r = new FileReader(); r.onload = (ev) => { try { const p = JSON.parse(ev.target?.result as string); if(p.nodes) { const loadedNodes = p.nodes.map((n:any) => ({...n, animScale: 1})); setNodes(loadedNodes); loadedNodes.forEach((n: Node) => { initialPositionsRef.current.set(n.id, { x: n.x, y: n.y }); }); } if(p.connections) setConnections(p.connections.map((c: any) => ({ ...c, lineType: c.lineType || 'curved', particleColor: c.particleColor || '#ffffff', particleSize: c.particleSize || 5 }))); if(p.library) setLibrary(p.library); if(p.backgroundColor) setBackgroundColor(p.backgroundColor); if(p.showGrid !== undefined) setShowGrid(p.showGrid); if(p.weatherValue !== undefined) setWeatherValue(p.weatherValue); if(p.timeOfDay !== undefined) setTimeOfDay(p.timeOfDay); if(p.backgroundSrc) { setBackgroundSrc(p.backgroundSrc); const img = new Image(); img.src = p.backgroundSrc; img.onload = () => setBackgroundImg(img); } else { setBackgroundSrc(null); setBackgroundImg(null); } } catch(err) { alert('Fout bij laden'); } }; r.readAsText(f); e.target.value = ''; };
  const deleteSel = () => { if(selectedEntity) { if(selectedEntity.type==='node') { setNodes(nodes.filter(n=>n.id!==selectedEntity.id)); setConnections(connections.filter(c=>c.fromId!==selectedEntity.id && c.toId!==selectedEntity.id)); initialPositionsRef.current.delete(selectedEntity.id); } else { setConnections(connections.filter(c=>c.id!==selectedEntity.id)); } setSelectedEntity(null); }};
  
  // Reset functions
  const resetPositions = () => {
    setNodes(nodes.map(n => {
      const initial = initialPositionsRef.current.get(n.id);
      return initial ? { ...n, x: initial.x, y: initial.y } : n;
    }));
    toast.success('Posities gereset');
    setShowResetMenu(false);
  };
  
  const resetColors = () => {
    setConnections(connections.map(c => ({
      ...c,
      color: '#60a5fa',
      particleColor: '#ffffff'
    })));
    toast.success('Kleuren gereset');
    setShowResetMenu(false);
  };
  
  // Undo/Redo handlers
  const handleUndo = () => {
    undoNodes();
    toast.success('Actie ongedaan gemaakt');
  };
  
  const handleRedo = () => {
    redoNodes();
    toast.success('Actie opnieuw uitgevoerd');
  };
  
  const resetSimulation = () => {
    setNodes(nodes.map(n => ({
      ...n,
      currentValue: n.baseValue,
      batteryCharge: 50,
      status: 'ok' as const
    })));
    setKpiData({ selfSufficiency: 0, costSaved: 0, co2Saved: 0 });
    setGraphHistory(new Array(60).fill({production: 0, consumption: 0, batteryLevel: 50}));
    toast.success('Simulatie gereset');
    setShowResetMenu(false);
  };
  
  const clearStorage = () => { 
    if(confirm('Alles verwijderen? Dit reset alle assets, kabels, kaart en instellingen.')) { 
      localStorage.removeItem('energyFlowState_sandbox'); 
      setNodes([]); 
      setConnections([]); 
      setLibrary({ solar: [], battery: [], grid: [], house: [], ev_charger: [], heatpump: [], custom: [] }); 
      setBackgroundImg(null); 
      setBackgroundSrc(null);
      // Reset all overlay settings
      setBgOverlayOffset({ x: 0, y: 0 });
      setBgOverlayScale(1);
      setBgOverlayOpacity(0.6);
      // Reset visual settings
      setBackgroundColor('auto');
      setWeatherValue(0);
      setTimeOfDay(12);
      setShowGrid(true);
      // Reset selection state
      setSelectedEntity(null);
      setSelectedNodeIds(new Set());
      setConnectionStart(null);
      // Clear caches
      imageCache.current.clear();
      initialPositionsRef.current.clear();
      // Close menu
      setShowResetMenu(false);
      toast.success('Alles gereset naar beginstand');
    } 
  };
  
  // Delete library item
  const deleteLibraryItem = (type: AssetType, index: number) => {
    const itemToDelete = library[type]?.[index];
    setLibrary(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
    
    // Reset active image if it was the deleted one
    if (activeImageSrc === itemToDelete) {
      setActiveImageSrc(null);
    }
    
    toast.success('Item verwijderd uit bibliotheek');
  };
  
  // Screenshot export
  const handleScreenshot = () => {
    const canvas = document.querySelector('canvas');
    if (!canvas) {
      toast.error('Geen canvas gevonden');
      return;
    }
    
    const link = document.createElement('a');
    link.download = `energy-flow-${viewMode}-${Date.now()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Screenshot opgeslagen');
  };
  
  // Video recording
  const startRecording = () => {
    const canvas = document.querySelector('canvas');
    if (!canvas) {
      toast.error('Geen canvas gevonden');
      return;
    }
    
    try {
      const stream = canvas.captureStream(30);
      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      
      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `energy-flow-recording-${Date.now()}.webm`;
        link.href = url;
        link.click();
        recordedChunksRef.current = [];
        toast.success('Video opgeslagen');
      };
      
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      toast.success('Opname gestart');
    } catch (err) {
      toast.error('Video opname niet ondersteund in deze browser');
    }
  };
  
  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };
  
  // GLTF upload handler
  const handleGLTFUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const type = uploadTypeRef.current || uploadingAssetType;
    if (!type) return;
    
    const file = e.target.files?.[0];
    if (!file) return;
    
    const isGLTF = file.name.match(/\.(glb|gltf)$/i);
    if (!isGLTF) {
      toast.error('Alleen .glb of .gltf bestanden zijn toegestaan');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      // Mark as GLTF by prefixing with special marker
      const gltfData = `gltf:${base64}`;
      setLibrary(prev => ({ ...prev, [type]: [...(prev[type] || []), gltfData] }));
      setSelectedTool(type);
      setActiveImageSrc(gltfData);
      toast.success('3D model toegevoegd aan bibliotheek');
      if (gltfInputRef.current) gltfInputRef.current.value = '';
    };
    reader.readAsDataURL(file);
  };
  
  const loadPreset = (preset: typeof PRESETS[0]) => {
      const newNodes = preset.nodes.map(n => {
        const id = Math.random().toString(36).substr(2,9);
        initialPositionsRef.current.set(id, { x: n.x, y: n.y });
        return {...n, id};
      });
      setNodes(newNodes); 
      setConnections(preset.connections.map(c => ({...c, id: Math.random().toString(36).substr(2,9)})));
  };
  
  // Save to CRM
  const handleSaveToCRM = async () => {
    if (!sceneName.trim()) {
      toast.error('Geef een naam op voor deze opstelling');
      return;
    }
    
    setIsSaving(true);
    try {
      await saveScene(
        sceneName.trim(),
        nodes,
        connections,
        { 
          backgroundColor, 
          weatherValue, 
          timeOfDay, 
          showGrid,
          bgOverlayOffset,
          bgOverlayScale,
          bgOverlayOpacity,
          backgroundSrc
        },
        sceneDescription.trim() || undefined
      );
      toast.success('Opstelling opgeslagen naar database!');
      setShowSaveDialog(false);
      setSceneName('');
      setSceneDescription('');
    } catch (err) {
      console.error('Save to CRM failed:', err);
      toast.error('Opslaan mislukt. Probeer opnieuw.');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Load from CRM
  const handleLoadScenes = async () => {
    setIsLoadingScenes(true);
    try {
      const scenes = await listScenes();
      setSavedScenes(scenes);
      setShowLoadDialog(true);
    } catch (err) {
      console.error('Load scenes failed:', err);
      toast.error('Kon opgeslagen opstellingen niet laden');
    } finally {
      setIsLoadingScenes(false);
    }
  };
  
  const handleLoadFromCRM = async (scene: SavedScene) => {
    try {
      // Load the full scene
      const fullScene = await loadScene(scene.id);
      if (!fullScene) {
        toast.error('Opstelling niet gevonden');
        return;
      }
      
      // Set nodes and connections
      setNodes(fullScene.nodes as Node[]);
      setConnections(fullScene.connections as Connection[]);
      
      // Set settings if available
      if (fullScene.settings) {
        const settings = fullScene.settings as any;
        if (settings.backgroundColor) setBackgroundColor(settings.backgroundColor);
        if (settings.weatherValue !== undefined) setWeatherValue(settings.weatherValue);
        if (settings.timeOfDay !== undefined) setTimeOfDay(settings.timeOfDay);
        if (settings.showGrid !== undefined) setShowGrid(settings.showGrid);
        if (settings.bgOverlayOffset) setBgOverlayOffset(settings.bgOverlayOffset);
        if (settings.bgOverlayScale !== undefined) setBgOverlayScale(settings.bgOverlayScale);
        if (settings.bgOverlayOpacity !== undefined) setBgOverlayOpacity(settings.bgOverlayOpacity);
        if (settings.backgroundSrc) {
          setBackgroundSrc(settings.backgroundSrc);
          const img = new Image();
          img.onload = () => setBackgroundImg(img);
          img.src = settings.backgroundSrc;
        }
      }
      
      // Store initial positions
      (fullScene.nodes as Node[]).forEach(n => {
        initialPositionsRef.current.set(n.id, { x: n.x, y: n.y });
      });
      
      toast.success(`"${fullScene.name}" geladen`);
      setShowLoadDialog(false);
    } catch (err) {
      console.error('Load from CRM failed:', err);
      toast.error('Laden mislukt');
    }
  };

  // In embed mode, render minimal UI
  if (embedMode) {
    return (
      <div ref={containerRef} className="w-full h-full overflow-hidden">
        <canvas 
          ref={canvasRef} 
          width={containerRef.current?.clientWidth || 800} 
          height={containerRef.current?.clientHeight || 600} 
          className="block w-full h-full cursor-pointer"
          onMouseMove={handleMouseMove}
          onMouseDown={handleMouseDown}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row h-full w-full bg-background text-foreground font-sans overflow-hidden select-none">
      
      {/* SIDEBAR - Redesigned with collapsible sections */}
      <div className="order-3 md:order-1 w-full md:w-16 bg-card border-t md:border-t-0 md:border-r border-border flex flex-row md:flex-col items-center py-2 md:py-3 gap-1 z-30 shrink-0 relative overflow-y-auto no-scrollbar">
        {/* Core Tools - Always visible */}
        <div className="flex md:flex-col gap-1 items-center px-1">
          <button 
            onClick={() => setSelectedTool('select')} 
            className={`p-2.5 rounded-lg transition-colors ${selectedTool==='select'?'bg-energy-green text-white shadow-md':'text-muted-foreground hover:bg-accent'}`}
            title="Selecteren"
          >
            <MousePointer2 size={18}/>
          </button>
          <button 
            onClick={() => setSelectedTool('connect')} 
            className={`p-2.5 rounded-lg transition-colors ${selectedTool==='connect'?'bg-energy-green text-white shadow-md':'text-muted-foreground hover:bg-accent'}`}
            title="Verbinden"
          >
            <Cable size={18}/>
          </button>
        </div>
        
        <div className="hidden md:block w-10 h-px bg-border my-1"></div>
        
        {/* Assets Section - Collapsible */}
        <div className="flex md:flex-col gap-1 items-center w-full px-1">
          <button 
            onClick={() => setCollapsedSections(p => ({...p, assets: !p.assets}))}
            className="hidden md:flex w-full items-center justify-center gap-1 py-1 text-[9px] font-bold uppercase text-muted-foreground hover:text-foreground transition-colors"
          >
            <span>Assets</span>
            <ChevronDown size={10} className={`transition-transform ${collapsedSections.assets ? '' : 'rotate-180'}`} />
          </button>
          
          {/* Library Manager Button - Always visible in Assets section */}
          <button 
            onClick={() => setShowLibraryManager(!showLibraryManager)} 
            className={`p-2 rounded-lg flex flex-col items-center gap-0.5 transition-colors w-full ${showLibraryManager ? 'text-energy-green bg-accent' : 'text-muted-foreground hover:bg-accent'}`}
            title="Asset Bibliotheek"
          >
            <Layers size={16}/>
            <span className="text-[8px] hidden md:block">Bibliotheek</span>
          </button>
          
          {!collapsedSections.assets && (
            <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible px-1 no-scrollbar w-full items-center">
              {ASSETS.map(a => (
                <div key={a.type} className="relative group flex justify-center" onMouseEnter={() => handleMouseEnterAsset(a.type)} onMouseLeave={handleMouseLeaveAsset}>
                  <button 
                    onClick={() => handleMainToolClick(a.type)} 
                    className={`p-2 rounded-lg flex flex-col items-center gap-0.5 transition-colors ${selectedTool===a.type ? 'bg-accent text-energy-green border border-energy-green/30' : 'text-muted-foreground hover:bg-accent'}`}
                    title={a.label}
                  >
                    {a.icon}
                    <span className="text-[8px] whitespace-nowrap hidden md:block">{a.label}</span>
                  </button>
                  {selectedTool === a.type && activeImageSrc && (
                    <div className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-energy-green rounded-full animate-pulse"></div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="hidden md:block w-10 h-px bg-border my-1"></div>
        
        {/* Scene Tools Section - Collapsible */}
        <div className="flex md:flex-col gap-1 items-center w-full px-1">
          <button 
            onClick={() => setCollapsedSections(p => ({...p, scene: !p.scene}))}
            className="hidden md:flex w-full items-center justify-center gap-1 py-1 text-[9px] font-bold uppercase text-muted-foreground hover:text-foreground transition-colors"
          >
            <span>Scene</span>
            <ChevronDown size={10} className={`transition-transform ${collapsedSections.scene ? '' : 'rotate-180'}`} />
          </button>
          
          {!collapsedSections.scene && (
            <div className="flex md:flex-col gap-1 items-center w-full">
              {/* Map/Background overlay control */}
              <button 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  setShowBgOverlayControls(!showBgOverlayControls); 
                  setShowColorPicker(false);
                  setShowResetMenu(false);
                }} 
                className={`p-2 rounded-lg flex flex-col items-center gap-0.5 transition-colors hover:bg-accent w-full ${backgroundSrc ? 'text-energy-green' : showBgOverlayControls ? 'text-energy-green bg-accent' : 'text-muted-foreground'}`} 
                title="Kaart/Achtergrond"
              >
                <MapIcon size={16}/>
                <span className="text-[8px] hidden md:block">Kaart</span>
              </button>
              <input type="file" ref={backgroundInputRef} className="hidden" accept="image/*" onChange={handleBackgroundUpload}/>
              
              {/* Color Picker Button */}
              <button 
                onClick={() => { 
                  setShowColorPicker(!showColorPicker); 
                  setShowBgOverlayControls(false);
                  setShowResetMenu(false);
                }}
                className={`p-2 rounded-lg flex flex-col items-center gap-0.5 transition-colors hover:bg-accent w-full ${showColorPicker ? 'text-energy-green bg-accent' : 'text-muted-foreground'}`}
                title="Achtergrondkleur"
              >
                <div 
                  className="w-4 h-4 rounded border border-border" 
                  style={{
                    backgroundColor: backgroundColor === 'auto' ? 'hsl(var(--muted))' : 
                                    backgroundColor === 'transparent' ? 'transparent' : backgroundColor,
                    backgroundImage: backgroundColor === 'transparent' ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)' : 'none',
                    backgroundSize: '8px 8px'
                  }}
                ></div>
                <span className="text-[8px] hidden md:block">Kleur</span>
              </button>
              
              <button 
                onClick={() => setShowGrid(!showGrid)} 
                className={`p-2 rounded-lg flex flex-col items-center gap-0.5 transition-colors w-full ${showGrid ? 'text-energy-green' : 'text-muted-foreground'} hover:bg-accent`}
                title="Grid aan/uit"
              >
                <Grid3x3 size={16}/>
                <span className="text-[8px] hidden md:block">Grid</span>
              </button>
            </div>
          )}
        </div>
        
        {/* Reset - At bottom */}
        <div className="hidden md:block flex-1"></div>
        <div className="relative flex justify-center px-1">
          <button 
            onClick={() => { setShowResetMenu(!showResetMenu); setShowBgOverlayControls(false); setShowColorPicker(false); }}
            className={`p-2 rounded-lg flex flex-col items-center gap-0.5 transition-colors hover:bg-accent ${showResetMenu ? 'text-destructive bg-accent' : 'text-destructive/50 hover:text-destructive'}`}
            title="Reset opties"
          >
            <RefreshCw size={14}/>
            <span className="text-[8px] hidden md:block">Reset</span>
          </button>
        </div>
      </div>
      
      
      <input 
        type="file" 
        ref={libraryInputRef} 
        className="hidden" 
        accept="image/*" 
        onChange={(e) => {
          if (!e.target.files?.length) {
            setIsUploading(false);
            setUploadingAssetType(null);
            return;
          }
          handleLibraryUpload(e);
        }}
      />
      <input 
        type="file" 
        ref={gltfInputRef} 
        className="hidden" 
        accept=".glb,.gltf" 
        onChange={handleGLTFUpload}
      />

      {/* CANVAS */}
      <div className="order-2 w-full flex-1 flex flex-col relative overflow-hidden">
          {/* Compact Header */}
          <div className="h-12 bg-card/95 backdrop-blur flex items-center justify-between px-3 z-20 shrink-0 border-b border-border">
             <div className="flex items-center gap-3">
                 <h1 className="font-bold text-sm hidden sm:block"><span className="text-energy-green">GV</span> Energy Flow</h1>
                 
                 {/* 2D/3D Mode Toggle */}
                 <div className="flex items-center bg-muted rounded-lg p-0.5">
                   <button 
                     onClick={() => {
                       if (viewMode !== '2d') {
                         setViewTransitioning(true);
                         setTimeout(() => {
                           setViewMode('2d');
                           setTimeout(() => setViewTransitioning(false), 300);
                         }, 200);
                       }
                     }}
                     className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-all ${
                       viewMode === '2d' ? 'bg-card text-energy-green shadow-sm' : 'text-muted-foreground hover:text-foreground'
                     }`}
                   >
                     <Layers size={12} />
                     2D
                   </button>
                   <button 
                     onClick={() => {
                       if (viewMode !== '3d') {
                         setViewTransitioning(true);
                         setTimeout(() => {
                           setViewMode('3d');
                           setTimeout(() => setViewTransitioning(false), 300);
                         }, 200);
                       }
                     }}
                     className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-all ${
                       viewMode === '3d' ? 'bg-card text-energy-green shadow-sm' : 'text-muted-foreground hover:text-foreground'
                     }`}
                   >
                     <Box size={12} />
                     3D
                   </button>
                 </div>
                 
                 {/* 3D-specific controls */}
                 {viewMode === '3d' && (
                   <div className="hidden sm:flex items-center gap-1">
                     <button onClick={() => setShowShadows(!showShadows)} className={`p-1.5 rounded transition-all ${showShadows ? 'text-energy-orange' : 'text-muted-foreground'}`} title="Schaduwen">
                       <SunIcon size={14} />
                     </button>
                     <button onClick={() => setShowEnergyStats(!showEnergyStats)} className={`p-1.5 rounded transition-all ${showEnergyStats ? 'text-energy-green' : 'text-muted-foreground'}`} title="Stats">
                       <BarChart3 size={14} />
                     </button>
                     <button onClick={() => setShowWeatherEffects(!showWeatherEffects)} className={`p-1.5 rounded transition-all ${showWeatherEffects ? 'text-energy-blue' : 'text-muted-foreground'}`} title="Weer">
                       <CloudRain size={14} />
                     </button>
                     <button onClick={() => setAutoDayNight(!autoDayNight)} className={`p-1.5 rounded transition-all ${autoDayNight ? 'text-energy-orange' : 'text-muted-foreground'}`} title="Auto cyclus">
                       <Clock size={14} />
                     </button>
                     
                     {/* Camera dropdown */}
                     <div className="relative">
                       <button onClick={() => setShowCameraMenu(!showCameraMenu)} className="flex items-center gap-1 px-2 py-1 rounded bg-muted text-xs">
                         <Camera size={12} />
                         <ChevronDown size={10} />
                       </button>
                       {showCameraMenu && (
                         <div className="absolute top-full mt-1 left-0 bg-popover border border-border rounded-lg shadow-xl z-[100] w-36 p-1">
                           {(Object.keys(CAMERA_PRESETS) as CameraPreset[]).map((preset) => (
                             <button
                               key={preset}
                               onClick={() => { setCameraPreset(preset); setShowCameraMenu(false); }}
                               className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 ${cameraPreset === preset ? 'bg-energy-green/10 text-energy-green' : 'hover:bg-accent'}`}
                             >
                               {CAMERA_PRESETS[preset].name}
                             </button>
                           ))}
                         </div>
                       )}
                     </div>
                   </div>
                 )}
                 
                 {/* Simulation toggle */}
                 <button 
                   onClick={() => {setIsSimulating(!isSimulating); if(!isSimulating) setAutoCycle(true);}} 
                   className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${isSimulating ? 'bg-energy-green text-white' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
                 >
                   {isSimulating ? <Activity size={12} className="animate-pulse"/> : <PlayCircle size={12}/>}
                   <span>{isSimulating ? 'LIVE' : 'Edit'}</span>
                 </button>
                 
                 {/* Time control - compact */}
                 <div className="hidden sm:flex items-center gap-1.5 bg-muted rounded-lg px-2 py-1" title="Tijd van dag">
                   <Clock size={12} className="text-energy-green"/>
                   <input type="range" min="0" max="24" step="0.1" value={timeOfDay} onChange={(e) => {setTimeOfDay(parseFloat(e.target.value)); setAutoCycle(false);}} className="w-16 h-1 bg-border rounded appearance-none cursor-pointer accent-energy-green"/>
                   <span className="text-[10px] w-8 font-mono text-foreground">{Math.floor(timeOfDay)}:00</span>
                 </div>
                 
                 {/* Weather button */}
                 <div className="relative">
                   <button onClick={() => setShowWeatherControl(!showWeatherControl)} className={`p-1.5 rounded transition-colors ${weatherValue > 50 ? 'text-energy-blue' : 'text-energy-orange'}`}>
                     {weatherValue < 30 ? <SunIcon size={14}/> : weatherValue < 70 ? <Cloud size={14}/> : <CloudRain size={14}/>}
                   </button>
                   {showWeatherControl && (
                     <div className="absolute top-8 left-0 bg-popover border border-border p-3 rounded-lg shadow-xl w-40 z-[100]">
                       <label className="text-[10px] font-bold text-muted-foreground mb-1 block">Weer</label>
                       <input type="range" min="0" max="100" value={weatherValue} onChange={(e) => setWeatherValue(parseInt(e.target.value))} className="w-full h-1.5 bg-muted rounded appearance-none cursor-pointer accent-energy-green"/>
                       <div className="flex justify-between text-[9px] text-muted-foreground mt-0.5"><span>Zon</span><span>Regen</span></div>
                     </div>
                   )}
                 </div>
             </div>
             
             {/* KPIs when simulating */}
             {isSimulating && (
               <div className="hidden lg:flex items-center gap-4 text-xs">
                 <div className="flex flex-col items-center">
                   <span className="text-[9px] text-muted-foreground uppercase">Zelfvoorziening</span>
                   <span className={`font-bold ${kpiData.selfSufficiency > 80 ? 'text-energy-green' : 'text-energy-blue'}`}>{kpiData.selfSufficiency.toFixed(0)}%</span>
                 </div>
                 <div className="flex flex-col items-center">
                   <span className="text-[9px] text-muted-foreground uppercase">Besparing</span>
                   <span className="font-bold text-energy-orange">€{kpiData.costSaved.toFixed(2)}</span>
                 </div>
                 <div className="flex flex-col items-center">
                   <span className="text-[9px] text-muted-foreground uppercase">CO2</span>
                   <span className="font-bold text-energy-green">{kpiData.co2Saved.toFixed(1)}kg</span>
                 </div>
               </div>
             )}

             {/* Right toolbar - minimal (Zoom only, rest in bottom bar) */}
             <div className="flex items-center gap-1">
               <button onClick={()=>setZoom(z=>Math.max(0.3, z-0.1))} className="p-1.5 text-muted-foreground hover:text-foreground" title="Zoom -"><ZoomOut size={14}/></button>
               <button onClick={()=>setZoom(z=>Math.min(3, z+0.1))} className="p-1.5 text-muted-foreground hover:text-foreground" title="Zoom +"><ZoomIn size={14}/></button>
               {selectedEntity && <button onClick={()=>setShowProperties(!showProperties)} className="md:hidden ml-1 p-1.5 bg-energy-green rounded text-white"><Menu size={14}/></button>}
             </div>
             <input type="file" ref={jsonImportRef} className="hidden" accept=".json" onChange={handleJSONImport}/>
          </div>
          <div ref={containerRef} className="flex-1 relative bg-muted/20 overflow-hidden cursor-crosshair touch-none pb-12"
               onMouseMove={viewMode === '2d' ? handleMouseMove : undefined} 
               onMouseDown={viewMode === '2d' ? handleMouseDown : undefined} 
               onMouseUp={viewMode === '2d' ? () => {
                 // Handle marquee selection end
                 if (isMarqueeSelecting && marqueeStart && marqueeEnd) {
                   const r = canvasRef.current?.getBoundingClientRect();
                   if (r) {
                     const minX = Math.min(marqueeStart.x, marqueeEnd.x) - r.left;
                     const maxX = Math.max(marqueeStart.x, marqueeEnd.x) - r.left;
                     const minY = Math.min(marqueeStart.y, marqueeEnd.y) - r.top;
                     const maxY = Math.max(marqueeStart.y, marqueeEnd.y) - r.top;
                     
                     const selected = new Set<string>();
                     nodes.forEach(node => {
                       const screen = isoToScreen(node.x, node.y, zoom, offset);
                       if (screen.x >= minX && screen.x <= maxX && screen.y >= minY && screen.y <= maxY) {
                         selected.add(node.id);
                       }
                     });
                     
                     if (selected.size > 0) {
                       setSelectedNodeIds(selected);
                       toast.success(`${selected.size} asset(s) geselecteerd`);
                     }
                   }
                    setIsMarqueeSelecting(false);
                    setMarqueeStart(null);
                    setMarqueeEnd(null);
                  }
                  // End multi-drag and commit to history
                  if (isDraggingSelection) {
                    setIsDraggingSelection(false);
                    setDragSelectionStart(null);
                    // Commit to history by setting nodes with history tracking
                    setNodes(nodes);
                    toast.success(`${selectedNodeIds.size} asset(s) verplaatst`);
                  }
                  setIsDragging(false);
                } : undefined} 
                onMouseLeave={viewMode === '2d' ? () => { setIsDragging(false); setIsMarqueeSelecting(false); setMarqueeStart(null); setMarqueeEnd(null); setIsDraggingSelection(false); setDragSelectionStart(null); } : undefined}>
               
               <div className={`w-full h-full transition-opacity duration-300 ${viewTransitioning ? 'opacity-0' : 'opacity-100'}`}>
                 {viewMode === '2d' ? (
                   <canvas ref={canvasRef} width={containerRef.current?.clientWidth||300} height={containerRef.current?.clientHeight||300} className="block w-full h-full" />
                 ) : (
                   <Suspense fallback={
                     <div className="flex items-center justify-center h-full bg-background">
                       <div className="flex flex-col items-center gap-4">
                         <div className="w-8 h-8 border-2 border-energy-green border-t-transparent rounded-full animate-spin" />
                         <span className="text-sm text-muted-foreground">3D omgeving laden...</span>
                       </div>
                     </div>
                   }>
                       <EnergyFlow3DView
                         nodes={nodes}
                         connections={connections}
                         selectedEntityId={selectedEntity?.id || null}
                         onSelectNode={(id) => setSelectedEntity(id ? { type: 'node', id } : null)}
                         onSelectConnection={(id) => setSelectedEntity(id ? { type: 'connection', id } : null)}
                         onUpdateNode={(id, updates) => setNodes(ns => ns.map(n => n.id === id ? { ...n, ...updates } : n))}
                         isSimulating={isSimulating}
                         timeOfDay={timeOfDay}
                         weatherValue={weatherValue}
                         showGrid={showGrid}
                         backgroundColor={backgroundColor}
                         showShadows={showShadows}
                         showEnergyStats={showEnergyStats}
                         showWeatherEffects={showWeatherEffects}
                         cameraPreset={cameraPreset}
                         selectedTool={selectedTool}
                         connectionStart={connectionStart3D}
                         onConnectionStart={(id) => {
                           setConnectionStart3D(id);
                           toast.info('Klik op tweede asset om verbinding te maken');
                         }}
                          onConnectionEnd={(toId) => {
                           if (connectionStart3D && connectionStart3D !== toId) {
                             const newConn: Connection = {
                               id: Math.random().toString(36).substr(2,9),
                               fromId: connectionStart3D,
                               toId,
                               color: '#22c55e',
                               speed: 3,
                               width: 4,
                               direction: 'forward',
                               shape: 'circle',
                               activeInDay: true,
                               activeInNight: true,
                               lineType: 'curved',
                               particleColor: '#ffffff',
                               particleSize: 2
                             };
                             setConnections(prev => [...prev, newConn]);
                             toast.success('Verbinding aangemaakt');
                           }
                           setConnectionStart3D(null);
                         }}
                         onPlaceAsset={(pos) => {
                           if (selectedTool && selectedTool !== 'select' && selectedTool !== 'connect') {
                             const def = ASSETS.find(a => a.type === selectedTool);
                             const assetDef = ASSET_DEFAULTS[selectedTool as AssetType];
                             const newNodeId = Math.random().toString(36).substr(2,9);
                              // Determine if activeImageSrc is a GLTF model (prefixed with 'gltf:')
                              const isGLTFModel = activeImageSrc?.startsWith('gltf:');
                              const gltfUrl = isGLTFModel ? activeImageSrc.substring(5) : undefined;
                              const imageUrl = !isGLTFModel ? activeImageSrc : null;
                              
                              const newNode: Node = {
                                id: newNodeId,
                                type: selectedTool as AssetType,
                                x: pos.x,
                                y: pos.y,
                                label: '',
                                baseValue: def ? def.defaultKw : 0,
                                customImageSrc: imageUrl,
                                customModelUrl: gltfUrl,
                                scale: assetDef?.defaultScale || 1,
                                batteryCharge: 50,
                                animScale: 0
                              };
                             setNodes(prev => [...prev, newNode]);
                             initialPositionsRef.current.set(newNodeId, { x: pos.x, y: pos.y });
                           }
                         }}
                       />
                   </Suspense>
                 )}
               </div>
               
               {/* Marquee selection overlay */}
               {isMarqueeSelecting && marqueeStart && marqueeEnd && (
                 <div 
                   className="absolute pointer-events-none border-2 border-dashed border-energy-green bg-energy-green/10 z-50"
                   style={{
                     left: Math.min(marqueeStart.x, marqueeEnd.x) - (containerRef.current?.getBoundingClientRect().left || 0),
                     top: Math.min(marqueeStart.y, marqueeEnd.y) - (containerRef.current?.getBoundingClientRect().top || 0),
                     width: Math.abs(marqueeEnd.x - marqueeStart.x),
                     height: Math.abs(marqueeEnd.y - marqueeStart.y),
                   }}
                 />
                )}
                
                {/* Asset Library Manager - positioned in canvas area */}
                <AssetLibraryManager
                  isOpen={showLibraryManager}
                  onClose={() => setShowLibraryManager(false)}
                  onSelectAsset={(asset, type) => {
                    if (asset) {
                      // Select asset for placement
                      setSelectedTool(type);
                      if (asset.kind === 'gltf') {
                        setActiveImageSrc(`gltf:${asset.publicUrl}`);
                      } else {
                        setActiveImageSrc(asset.publicUrl);
                      }
                    }
                    setShowLibraryManager(false);
                  }}
                />
                
                {/* ========== POPUP OVERLAY LAYER - z-[200] ensures it's above canvas/3D ========== */}
                
                {/* MAP/KAART POPUP */}
                {showBgOverlayControls && (
                  <div className="absolute left-4 top-16 z-[200] animate-in slide-in-from-left-2 fade-in duration-200 pointer-events-auto">
                    <div className="bg-popover border border-border rounded-xl shadow-2xl p-4 w-64">
                      <div className="flex justify-between items-center mb-3 border-b border-border pb-2">
                        <h3 className="font-bold text-sm text-foreground uppercase tracking-wider">Kaart / Overlay</h3>
                        <button 
                          onClick={() => setShowBgOverlayControls(false)} 
                          className="text-muted-foreground hover:text-foreground transition-colors p-1 hover:bg-accent rounded"
                        >
                          <X size={14}/>
                        </button>
                      </div>
                      
                      {/* Upload button */}
                      <button 
                        onClick={() => backgroundInputRef.current?.click()}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-border hover:border-energy-green hover:bg-accent text-muted-foreground hover:text-energy-green transition-colors mb-3"
                      >
                        <Upload size={16}/>
                        <span className="text-sm">{backgroundSrc ? 'Vervangen' : 'Upload Drone/Luchtfoto'}</span>
                      </button>
                      
                      {backgroundSrc && (
                        <>
                          {/* Pan mode toggle */}
                          <div className="flex items-center justify-between py-2 border-t border-border">
                            <span className="text-xs text-muted-foreground">Pan modus</span>
                            <button
                              onClick={() => setEditingBgOverlay(!editingBgOverlay)}
                              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${editingBgOverlay ? 'bg-energy-green text-white' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
                            >
                              {editingBgOverlay ? 'Actief' : 'Uit'}
                            </button>
                          </div>
                          
                          {/* Opacity slider */}
                          <div className="py-2 border-t border-border">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs text-muted-foreground">Transparantie</span>
                              <span className="text-xs font-mono text-foreground">{Math.round(bgOverlayOpacity * 100)}%</span>
                            </div>
                            <input 
                              type="range" 
                              min="0.1" 
                              max="1" 
                              step="0.05" 
                              value={bgOverlayOpacity} 
                              onChange={(e) => setBgOverlayOpacity(parseFloat(e.target.value))}
                              className="w-full h-1.5 bg-muted rounded appearance-none cursor-pointer accent-energy-green"
                            />
                          </div>
                          
                          {/* Scale slider */}
                          <div className="py-2 border-t border-border">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs text-muted-foreground">Schaal</span>
                              <span className="text-xs font-mono text-foreground">{bgOverlayScale.toFixed(2)}x</span>
                            </div>
                            <input 
                              type="range" 
                              min="0.1" 
                              max="3" 
                              step="0.05" 
                              value={bgOverlayScale} 
                              onChange={(e) => setBgOverlayScale(parseFloat(e.target.value))}
                              className="w-full h-1.5 bg-muted rounded appearance-none cursor-pointer accent-energy-green"
                            />
                          </div>
                          
                          {/* Reset kaart button */}
                          <button 
                            onClick={() => {
                              setBackgroundSrc(null);
                              setBackgroundImg(null);
                              setBgOverlayOffset({ x: 0, y: 0 });
                              setBgOverlayScale(1);
                              setBgOverlayOpacity(0.6);
                              setEditingBgOverlay(false);
                              setShowBgOverlayControls(false);
                            }}
                            className="w-full mt-2 py-2 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 text-xs font-medium transition-colors"
                          >
                            Verwijder Kaart
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                {/* COLOR PICKER POPUP */}
                {showColorPicker && (
                  <div className="absolute left-4 top-32 z-[200] animate-in slide-in-from-left-2 fade-in duration-200 pointer-events-auto">
                    <div className="bg-popover border border-border rounded-xl shadow-2xl p-4 w-56">
                      <div className="flex justify-between items-center mb-3 border-b border-border pb-2">
                        <h3 className="font-bold text-sm text-foreground uppercase tracking-wider">Achtergrondkleur</h3>
                        <button 
                          onClick={() => setShowColorPicker(false)} 
                          className="text-muted-foreground hover:text-foreground transition-colors p-1 hover:bg-accent rounded"
                        >
                          <X size={14}/>
                        </button>
                      </div>
                      
                      {/* Quick presets */}
                      <div className="flex gap-2 mb-3">
                        <button
                          onClick={() => { setBackgroundColor('auto'); setShowColorPicker(false); }}
                          className={`flex-1 py-2 rounded text-xs font-medium transition-colors ${backgroundColor === 'auto' ? 'bg-energy-green text-white' : 'bg-muted hover:bg-accent text-muted-foreground'}`}
                        >
                          Auto
                        </button>
                        <button
                          onClick={() => { setBackgroundColor('transparent'); setShowColorPicker(false); }}
                          className={`flex-1 py-2 rounded text-xs font-medium transition-colors ${backgroundColor === 'transparent' ? 'bg-energy-green text-white' : 'bg-muted hover:bg-accent text-muted-foreground'}`}
                        >
                          Transparant
                        </button>
                      </div>
                      
                      {/* Color presets */}
                      <div className="grid grid-cols-6 gap-1.5 mb-3">
                        {['#0f172a', '#1e293b', '#334155', '#f8fafc', '#22c55e', '#3b82f6', '#f97316', '#ef4444'].map(color => (
                          <button
                            key={color}
                            onClick={() => { setBackgroundColor(color); setCustomHexColor(color); }}
                            className={`w-7 h-7 rounded border-2 transition-all ${backgroundColor === color ? 'border-energy-green scale-110' : 'border-transparent hover:border-border'}`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      
                      {/* Custom color picker */}
                      <div className="flex gap-2 items-center border-t border-border pt-3">
                        <input
                          ref={colorInputRef}
                          type="color"
                          value={customHexColor}
                          onChange={(e) => setCustomHexColor(e.target.value)}
                          className="w-10 h-8 rounded cursor-pointer border-0 p-0"
                        />
                        <input
                          type="text"
                          value={customHexColor}
                          onChange={(e) => setCustomHexColor(e.target.value)}
                          className="flex-1 bg-muted border border-border rounded px-2 py-1.5 text-xs font-mono text-foreground"
                          placeholder="#000000"
                        />
                        <button
                          onClick={() => { setBackgroundColor(customHexColor); setShowColorPicker(false); }}
                          className="p-1.5 rounded bg-energy-green text-white hover:bg-energy-green/90 transition-colors"
                        >
                          <Check size={14}/>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* RESET MENU POPUP */}
                {showResetMenu && (
                  <div className="absolute left-4 bottom-16 z-[200] animate-in slide-in-from-left-2 fade-in duration-200 pointer-events-auto">
                    <div className="bg-popover border border-border rounded-xl shadow-2xl p-3 w-48">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => {
                            setNodes([]);
                            setConnections([]);
                            setSelectedEntity(null);
                            setSelectedNodeIds(new Set());
                            setBackgroundSrc(null);
                            setBackgroundImg(null);
                            setBackgroundColor('auto');
                            setBgOverlayOffset({ x: 0, y: 0 });
                            setBgOverlayScale(1);
                            setBgOverlayOpacity(0.6);
                            setShowResetMenu(false);
                            toast.success('Sandbox gereset');
                          }}
                          className="w-full px-3 py-2 text-left text-sm rounded hover:bg-destructive/10 text-destructive transition-colors flex items-center gap-2"
                        >
                          <Trash2 size={14}/>
                          Verwijder Alles
                        </button>
                        <button
                          onClick={() => {
                            setBackgroundSrc(null);
                            setBackgroundImg(null);
                            setBgOverlayOffset({ x: 0, y: 0 });
                            setBgOverlayScale(1);
                            setBgOverlayOpacity(0.6);
                            setShowResetMenu(false);
                            toast.success('Kaart verwijderd');
                          }}
                          className="w-full px-3 py-2 text-left text-sm rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                        >
                          <MapIcon size={14}/>
                          Reset Kaart
                        </button>
                        <button
                          onClick={() => {
                            setBackgroundColor('auto');
                            setShowResetMenu(false);
                            toast.success('Kleur gereset');
                          }}
                          className="w-full px-3 py-2 text-left text-sm rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                        >
                          <Palette size={14}/>
                          Reset Kleur
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* ASSET LIBRARY POPUP - Inside canvas container */}
                {(hoveredAssetType || isUploading || uploadingAssetType) && (
                  <div 
                    className="absolute left-4 top-4 z-[60] flex items-start animate-in slide-in-from-left-2 fade-in duration-200" 
                    onMouseEnter={() => { if(hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current); }} 
                    onMouseLeave={() => { if (!isUploading) setHoveredAssetType(null); }}
                  >
                    <div className="bg-popover border border-border rounded-xl shadow-2xl p-4 w-72">
                      <div className="flex justify-between items-center mb-3 border-b border-border pb-2">
                        <h3 className="font-bold text-sm text-foreground uppercase tracking-wider">{ASSETS.find(a => a.type === (hoveredAssetType || uploadingAssetType))?.label || 'Asset'} Bibliotheek</h3>
                        <button 
                          onClick={() => { 
                            setHoveredAssetType(null); 
                            setIsUploading(false); 
                            setUploadingAssetType(null); 
                          }} 
                          className="text-muted-foreground hover:text-foreground transition-colors p-1 hover:bg-accent rounded"
                          title="Sluiten (Escape)"
                        >
                          <X size={14}/>
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-2 max-h-[50vh] overflow-y-auto custom-scrollbar">
                        {/* Default item */}
                        <button onClick={() => { const type = hoveredAssetType || uploadingAssetType; if (type) selectLibraryItem(type, null); }} className={`aspect-square rounded border flex flex-col items-center justify-center gap-1 hover:bg-accent transition-colors ${selectedTool === (hoveredAssetType || uploadingAssetType) && activeImageSrc === null ? 'border-energy-green bg-accent' : 'border-border bg-card'}`}><div className="text-muted-foreground">{ASSETS.find(a => a.type === (hoveredAssetType || uploadingAssetType))?.icon}</div><span className="text-[8px] text-muted-foreground">Standaard</span></button>
                        
                        {/* Library items with delete button and 3D badge */}
                        {((hoveredAssetType || uploadingAssetType) ? library[hoveredAssetType || uploadingAssetType!] || [] : []).map((src, idx) => {
                          const isGLTF = src.startsWith('gltf:') || src.startsWith('data:model/gltf') || src.startsWith('data:application/octet-stream');
                          const actualSrc = src.startsWith('gltf:') ? src.substring(5) : src;
                          const currentType = hoveredAssetType || uploadingAssetType;
                          
                          return (
                            <button 
                              key={idx} 
                              onClick={() => { if (currentType) selectLibraryItem(currentType, src); }} 
                              className={`aspect-square rounded border relative overflow-hidden group ${selectedTool === currentType && activeImageSrc === src ? 'border-energy-green' : 'border-border'}`}
                            >
                              {isGLTF ? (
                                <div className="flex flex-col items-center justify-center h-full bg-muted">
                                  <Box size={24} className="text-energy-green" />
                                  <span className="text-[8px] text-energy-green font-bold mt-1">3D Model</span>
                                </div>
                              ) : (
                                <img src={actualSrc} className="w-full h-full object-cover" alt="asset" />
                              )}
                              
                              {/* 3D Badge */}
                              {isGLTF && (
                                <span className="absolute top-0.5 right-0.5 bg-energy-green text-white text-[7px] px-1 rounded font-bold">
                                  3D
                                </span>
                              )}
                              
                              {/* Selection indicator */}
                              {selectedTool === currentType && activeImageSrc === src && (
                                <div className="absolute inset-0 bg-energy-green/20 flex items-center justify-center">
                                  <Check size={16} className="text-white drop-shadow-md"/>
                                </div>
                              )}
                              
                              {/* Delete button on hover */}
                              <button 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  if (currentType) deleteLibraryItem(currentType, idx); 
                                }}
                                className="absolute top-0.5 left-0.5 opacity-0 group-hover:opacity-100 bg-destructive/90 rounded p-0.5 transition-opacity"
                                title="Verwijderen uit bibliotheek"
                              >
                                <X size={10} className="text-white" />
                              </button>
                            </button>
                          );
                        })}
                      </div>
                      
                      {/* Upload buttons - separate for Image and GLTF */}
                      <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                        <button 
                          onClick={() => { 
                            const type = hoveredAssetType || uploadingAssetType; 
                            if (type) { 
                              uploadTypeRef.current = type; 
                              setUploadingAssetType(type); 
                              setIsUploading(true); 
                              libraryInputRef.current?.click(); 
                            } 
                          }} 
                          disabled={isUploading} 
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded border border-dashed border-border hover:border-energy-green hover:bg-accent text-muted-foreground hover:text-energy-green transition-colors disabled:opacity-50 text-xs"
                        >
                          <ImageIcon size={14} />
                          <span>Afbeelding</span>
                        </button>
                        
                        <button 
                          onClick={() => { 
                            const type = hoveredAssetType || uploadingAssetType; 
                            if (type) { 
                              uploadTypeRef.current = type; 
                              setUploadingAssetType(type); 
                              gltfInputRef.current?.click(); 
                            } 
                          }} 
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded border border-dashed border-energy-green/50 hover:border-energy-green hover:bg-energy-green/10 text-energy-green transition-colors text-xs"
                        >
                          <Box size={14} />
                          <span>3D Model</span>
                        </button>
                      </div>
                      
                      {isUploading && (
                        <div className="flex items-center gap-2 text-energy-green text-xs mt-3 pt-2 border-t border-border">
                          <div className="w-3 h-3 border-2 border-energy-green border-t-transparent rounded-full animate-spin"></div>
                          Uploaden...
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* RESET MENU POPUP - Inside canvas container for proper z-index */}
                {showResetMenu && (
                  <div className="absolute left-4 bottom-16 z-[200] w-48 bg-popover border border-border rounded-xl shadow-2xl p-2 animate-in slide-in-from-left-2">
                    <button onClick={resetPositions} className="w-full text-left px-3 py-2 rounded hover:bg-accent text-sm flex items-center gap-2"><RotateCcw size={14}/> Reset Posities</button>
                    <button onClick={resetColors} className="w-full text-left px-3 py-2 rounded hover:bg-accent text-sm flex items-center gap-2"><Palette size={14}/> Reset Kleuren</button>
                    <button onClick={resetSimulation} className="w-full text-left px-3 py-2 rounded hover:bg-accent text-sm flex items-center gap-2"><Activity size={14}/> Reset Simulatie</button>
                    <div className="border-t border-border my-1"></div>
                    <button 
                      onClick={() => { 
                        setBackgroundSrc(null); 
                        setBackgroundImg(null);
                        setBgOverlayOffset({ x: 0, y: 0 });
                        setBgOverlayScale(1);
                        setBgOverlayOpacity(0.6);
                        setShowResetMenu(false);
                        toast.success('Kaart verwijderd');
                      }} 
                      className="w-full text-left px-3 py-2 rounded hover:bg-accent text-sm flex items-center gap-2"
                    >
                      <MapIcon size={14}/> Reset Kaart
                    </button>
                    <div className="border-t border-border my-1"></div>
                    <button onClick={clearStorage} className="w-full text-left px-3 py-2 rounded hover:bg-destructive/20 text-destructive text-sm flex items-center gap-2"><Eraser size={14}/> Verwijder Alles</button>
                  </div>
                )}
                
                {/* BOTTOM TOOLBAR - Always visible */}
               <div className="absolute bottom-0 left-0 right-0 h-12 bg-card/95 backdrop-blur border-t border-border flex items-center justify-between px-4 z-30">
                 {/* Left: Undo/Redo */}
                 <div className="flex items-center gap-1">
                   <button onClick={handleUndo} disabled={!canUndo} className="p-2 hover:bg-accent rounded disabled:opacity-30 text-muted-foreground hover:text-foreground transition-colors" title="Ongedaan maken (Ctrl+Z)">
                     <Undo size={16} />
                   </button>
                   <button onClick={handleRedo} disabled={!canRedo} className="p-2 hover:bg-accent rounded disabled:opacity-30 text-muted-foreground hover:text-foreground transition-colors" title="Opnieuw (Ctrl+Shift+Z)">
                     <Redo size={16} />
                   </button>
                 </div>
                 
                 {/* Center: Capture */}
                 <div className="flex items-center gap-1">
                   <button onClick={handleScreenshot} className="p-2 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors" title="Screenshot">
                     <Camera size={16} />
                   </button>
                   {viewMode === '3d' && (
                     <button 
                       onClick={isRecording ? stopRecording : startRecording}
                       className={`p-2 rounded transition-colors ${isRecording ? 'bg-destructive text-white animate-pulse' : 'hover:bg-accent text-muted-foreground hover:text-foreground'}`}
                       title={isRecording ? 'Stop opname' : 'Video opnemen'}
                     >
                       <Video size={16} />
                     </button>
                   )}
                 </div>
                 
                 {/* Right: Import/Export/Save */}
                 <div className="flex items-center gap-1">
                   <button onClick={() => jsonImportRef.current?.click()} className="p-2 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors" title="Import JSON">
                     <Upload size={16} />
                   </button>
                   <button onClick={handleJSONExport} className="p-2 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors" title="Export JSON">
                     <Download size={16} />
                   </button>
                   <div className="w-px h-6 bg-border mx-1" />
                   <button onClick={() => setShowSaveDialog(true)} className="p-2 hover:bg-accent rounded text-energy-green hover:bg-energy-green/10 transition-colors" title="Opslaan naar CRM">
                     <Database size={16} />
                   </button>
                   <button onClick={handleLoadScenes} disabled={isLoadingScenes} className="p-2 hover:bg-accent rounded text-muted-foreground hover:text-energy-green disabled:opacity-50 transition-colors" title="Laden uit CRM">
                     {isLoadingScenes ? <div className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" /> : <FolderOpen size={16} />}
                   </button>
                 </div>
               </div>
               
               {isSimulating && viewMode === '2d' && (
                   <div className="absolute bottom-12 left-0 right-0 h-32 bg-card/90 backdrop-blur border-t border-border p-4 flex items-center gap-4 animate-in slide-in-from-bottom-10 fade-in duration-300">
                       <div className="flex flex-col w-32 shrink-0">
                           <h3 className="text-xs font-bold text-muted-foreground uppercase mb-2 flex items-center gap-1"><TrendingUp size={14}/> Realtime</h3>
                           <div className="flex items-center gap-2 mb-1"><div className="w-2 h-2 rounded-full bg-energy-green"></div><span className="text-xs text-foreground">Opwek</span></div>
                           <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-destructive"></div><span className="text-xs text-foreground">Verbruik</span></div>
                       </div>
                       <canvas ref={graphCanvasRef} width={600} height={100} className="flex-1 h-full w-full bg-muted/30 rounded border border-border" />
                   </div>
               )}
          </div>
      </div>

      {/* PROPERTIES */}
      {selectedEntity && (
        <div className={`fixed inset-y-0 right-0 w-80 bg-card border-l border-border shadow-2xl z-40 transform transition-transform duration-300 ${showProperties ? 'translate-x-0' : 'translate-x-full md:translate-x-0'} md:relative md:transform-none`}>
            <div className="flex items-center justify-between p-4 border-b border-border">
                <h2 className="font-bold text-xs uppercase text-muted-foreground">Eigenschappen</h2>
                <button onClick={() => setSelectedEntity(null)} className="text-muted-foreground hover:text-foreground transition-colors" title="Sluiten"><X size={18}/></button>
            </div>
            <div className="p-4 space-y-6 overflow-y-auto h-full pb-20">
                {selectedEntity.type === 'node' ? (() => {
                    const n = nodes.find(x=>x.id===selectedEntity.id); if(!n) return null;
                    return (
                        <>
                           <div><label className="text-[10px] font-bold text-muted-foreground uppercase block mb-1">Naam</label><input type="text" value={n.label||''} onChange={e=>setNodes(nodes.map(x=>x.id===n.id?{...x,label:e.target.value}:x))} className="w-full bg-input border border-border rounded px-3 py-2 text-sm text-foreground"/></div>
                           <div><label className="text-[10px] font-bold text-muted-foreground uppercase block mb-1">Basis kW</label><input type="number" value={n.baseValue||0} onChange={e=>setNodes(nodes.map(x=>x.id===n.id?{...x,baseValue:parseFloat(e.target.value)}:x))} className="w-full bg-input border border-border rounded px-3 py-2 text-sm text-foreground"/></div>
                           
                           {/* Scale slider for ALL assets */}
                           <div>
                             <label className="text-[10px] font-bold text-muted-foreground uppercase block mb-1">Schaal ({(n.scale || ASSET_DEFAULTS[n.type]?.defaultScale || 1).toFixed(1)}x)</label>
                             <input type="range" min="0.2" max="3" step="0.1" value={n.scale || ASSET_DEFAULTS[n.type]?.defaultScale || 1} onChange={e=>setNodes(nodes.map(x=>x.id===n.id?{...x,scale:parseFloat(e.target.value)}:x))} className="w-full accent-energy-green"/>
                           </div>
                           
                           {/* Click URL for embed mode */}
                           <div>
                             <label className="text-[10px] font-bold text-muted-foreground uppercase block mb-1 flex items-center gap-1"><LinkIcon size={10}/> Klik URL</label>
                             <input type="url" value={n.clickUrl||''} onChange={e=>setNodes(nodes.map(x=>x.id===n.id?{...x,clickUrl:e.target.value}:x))} placeholder="https://greenvision.energy/..." className="w-full bg-input border border-border rounded px-3 py-2 text-sm text-foreground"/>
                           </div>
                           {n.clickUrl && (
                             <div>
                               <label className="text-[10px] font-bold text-muted-foreground uppercase block mb-1">Link Target</label>
                               <select value={n.clickTarget||'_blank'} onChange={e=>setNodes(nodes.map(x=>x.id===n.id?{...x,clickTarget:e.target.value as '_self'|'_blank'|'_parent'}:x))} className="w-full bg-input border border-border rounded px-3 py-2 text-sm text-foreground">
                                 <option value="_blank">Nieuw tabblad</option>
                                 <option value="_self">Zelfde venster</option>
                                 <option value="_parent">Parent frame</option>
                               </select>
                             </div>
                           )}
                           
                           <button onClick={deleteSel} className="w-full flex items-center justify-center gap-2 bg-destructive/20 text-destructive hover:bg-destructive/30 py-2 rounded font-medium text-sm transition-colors"><Trash2 size={16}/> Verwijderen</button>
                        </>
                    );
                })() : (() => {
                    const c = connections.find(x=>x.id===selectedEntity.id); if(!c) return null;
                    return (
                        <>
                           <div><label className="text-[10px] font-bold text-muted-foreground uppercase block mb-1">Lijn Type</label>
                             <select value={c.lineType || 'curved'} onChange={e=>setConnections(connections.map(x=>x.id===c.id?{...x,lineType:e.target.value as LineType}:x))} className="w-full bg-input border border-border rounded px-3 py-2 text-sm text-foreground">
                               <option value="curved">Gebogen</option>
                               <option value="straight">Recht</option>
                               <option value="orthogonal">Haaks (90°)</option>
                             </select>
                           </div>
                           <div><label className="text-[10px] font-bold text-muted-foreground uppercase block mb-1">Lijn Kleur</label><input type="color" value={c.color} onChange={e=>setConnections(connections.map(x=>x.id===c.id?{...x,color:e.target.value}:x))} className="w-full h-10 rounded bg-input border border-border"/></div>
                           <div><label className="text-[10px] font-bold text-muted-foreground uppercase block mb-1">Bolletje Kleur</label><input type="color" value={c.particleColor || '#ffffff'} onChange={e=>setConnections(connections.map(x=>x.id===c.id?{...x,particleColor:e.target.value}:x))} className="w-full h-10 rounded bg-input border border-border"/></div>
                           <div><label className="text-[10px] font-bold text-muted-foreground uppercase block mb-1">Bolletje Grootte ({c.particleSize || 5})</label><input type="range" min="2" max="15" step="1" value={c.particleSize || 5} onChange={e=>setConnections(connections.map(x=>x.id===c.id?{...x,particleSize:parseInt(e.target.value)}:x))} className="w-full accent-energy-green"/></div>
                           <div><label className="text-[10px] font-bold text-muted-foreground uppercase block mb-1">Snelheid</label><input type="range" min="0.5" max="10" step="0.5" value={c.speed} onChange={e=>setConnections(connections.map(x=>x.id===c.id?{...x,speed:parseFloat(e.target.value)}:x))} className="w-full accent-energy-green"/></div>
                           <div><label className="text-[10px] font-bold text-muted-foreground uppercase block mb-1">Dikte</label><input type="range" min="1" max="12" step="1" value={c.width} onChange={e=>setConnections(connections.map(x=>x.id===c.id?{...x,width:parseInt(e.target.value)}:x))} className="w-full accent-energy-green"/></div>
                           <div><label className="text-[10px] font-bold text-muted-foreground uppercase block mb-1">Richting</label><select value={c.direction} onChange={e=>setConnections(connections.map(x=>x.id===c.id?{...x,direction:e.target.value as 'forward'|'reverse'}:x))} className="w-full bg-input border border-border rounded px-3 py-2 text-sm text-foreground"><option value="forward">Voorwaarts</option><option value="reverse">Achterwaarts</option></select></div>
                           <div><label className="text-[10px] font-bold text-muted-foreground uppercase block mb-1">Vorm</label><select value={c.shape} onChange={e=>setConnections(connections.map(x=>x.id===c.id?{...x,shape:e.target.value as FlowShape}:x))} className="w-full bg-input border border-border rounded px-3 py-2 text-sm text-foreground"><option value="dash">Streep</option><option value="circle">Cirkel</option><option value="square">Vierkant</option></select></div>
                           <div className="flex gap-4"><label className="flex items-center gap-2 text-xs text-foreground"><input type="checkbox" checked={c.activeInDay} onChange={e=>setConnections(connections.map(x=>x.id===c.id?{...x,activeInDay:e.target.checked}:x))} className="accent-energy-green" /> Dag</label><label className="flex items-center gap-2 text-xs text-foreground"><input type="checkbox" checked={c.activeInNight} onChange={e=>setConnections(connections.map(x=>x.id===c.id?{...x,activeInNight:e.target.checked}:x))} className="accent-energy-green" /> Nacht</label></div>
                           <button onClick={deleteSel} className="w-full flex items-center justify-center gap-2 bg-destructive/20 text-destructive hover:bg-destructive/30 py-2 rounded font-medium text-sm transition-colors"><Trash2 size={16}/> Verwijderen</button>
                        </>
                    );
                })()}
            </div>
        </div>
      )}
      
      {/* SAVE DIALOG - Positioned with high z-index to appear above 3D canvas */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-lg flex items-center gap-2">
                <Database size={20} className="text-energy-green" />
                Opstelling Opslaan
              </h2>
              <button onClick={() => setShowSaveDialog(false)} className="text-muted-foreground hover:text-foreground">
                <X size={18}/>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase block mb-1">Naam *</label>
                <input 
                  type="text"
                  placeholder="Bijv. Hoofdkantoor Zonnepanelen" 
                  value={sceneName}
                  onChange={e => setSceneName(e.target.value)}
                  className="w-full bg-input border border-border rounded-lg px-4 py-3 text-foreground focus:border-energy-green focus:ring-1 focus:ring-energy-green outline-none"
                  autoFocus
                />
              </div>
              
              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase block mb-1">Beschrijving (optioneel)</label>
                <textarea 
                  placeholder="Voeg een beschrijving toe..."
                  value={sceneDescription}
                  onChange={e => setSceneDescription(e.target.value)}
                  className="w-full bg-input border border-border rounded-lg px-4 py-3 text-foreground h-24 resize-none focus:border-energy-green focus:ring-1 focus:ring-energy-green outline-none"
                />
              </div>
              
              <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
                <p className="flex items-center gap-2">
                  <Check size={14} className="text-energy-green" />
                  {nodes.length} asset(s) en {connections.length} verbinding(en) worden opgeslagen
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button 
                onClick={() => setShowSaveDialog(false)} 
                className="flex-1 py-3 border border-border rounded-lg font-medium hover:bg-accent transition-colors"
              >
                Annuleren
              </button>
              <button 
                onClick={handleSaveToCRM} 
                disabled={isSaving || !sceneName.trim()}
                className="flex-1 py-3 bg-energy-green text-white rounded-lg font-medium hover:bg-energy-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Opslaan...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Opslaan
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* LOAD DIALOG - Positioned with high z-index to appear above 3D canvas */}
      {showLoadDialog && (
        <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-lg max-h-[80vh] flex flex-col shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-lg flex items-center gap-2">
                <FolderOpen size={20} className="text-energy-green" />
                Opgeslagen Opstellingen
              </h2>
              <button onClick={() => setShowLoadDialog(false)} className="text-muted-foreground hover:text-foreground">
                <X size={18}/>
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto space-y-2 min-h-[200px]">
              {savedScenes.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Database size={48} className="text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground font-medium">Geen opgeslagen opstellingen</p>
                  <p className="text-xs text-muted-foreground mt-1">Sla eerst een opstelling op om deze hier te zien</p>
                </div>
              ) : (
                savedScenes.map(scene => (
                  <button 
                    key={scene.id}
                    onClick={() => handleLoadFromCRM(scene)}
                    className="w-full text-left p-4 rounded-lg border border-border hover:border-energy-green hover:bg-accent/50 transition-all group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-foreground block truncate group-hover:text-energy-green transition-colors">
                          {scene.name}
                        </span>
                        {scene.description && (
                          <span className="text-xs text-muted-foreground block mt-1 line-clamp-2">
                            {scene.description}
                          </span>
                        )}
                        <span className="text-[10px] text-muted-foreground mt-2 block">
                          {new Date(scene.createdAt).toLocaleDateString('nl-NL', { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <ExternalLink size={16} className="text-muted-foreground group-hover:text-energy-green flex-shrink-0 ml-2 mt-1" />
                    </div>
                  </button>
                ))
              )}
            </div>
            
            <button 
              onClick={() => setShowLoadDialog(false)} 
              className="w-full py-3 border border-border rounded-lg font-medium hover:bg-accent transition-colors mt-4"
            >
              Sluiten
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
